import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import { v4 as uuidv4 } from 'uuid';

import { FieldsError, ForbiddenError, StreamVendorError } from '~/Api/Error';
import { MetaDataResponseType, SORT_ORDER } from '~/Api/types';
import { UpdatePaymentTokenReason } from '~/components/organisms/SolidCheckout/types';
import { decrypt } from '~/helpers/simpleEncryption';
import { UsageStatisticsType } from '~/modules/cancellation/types';
import { MessageType } from '~/modules/chat/components/Chat/elements/Message/constants';
import {
  CHAT_TYPE,
  ChatItemType,
  DTOMessageItemType,
  MESSAGE_CONTEXT,
  MessageItemType,
  PERSONALITY,
  RecommendedChatPromoToolType,
} from '~/modules/chat/types';
import { PolicyType } from '~/modules/feature-access/policy';
import { GrammarCheckResult } from '~/modules/grammar-checker/src';
import {
  ImageGenerationExecutionItemType,
  FormDataType as ImageGenerationFormDataType,
} from '~/modules/image-generator/types';
import { PrepaidBenefits } from '~/modules/prepaid/constants';
import { REACTION } from '~/modules/reactions/types';
import { addTrimValidationToScheme } from '~/modules/tools/helpers/validation';
import {
  AiEditType,
  ArticleExecutionType,
  CardColor,
  ExtendedToolType,
  GroupToolsType,
  OutlineItemType,
  ToolCategoryType,
  ToolExecutionType,
  ToolType,
  UiFieldType,
} from '~/modules/tools/types';
import {
  ArticleOutlineType,
  ArticleType,
} from '~/modules/tools/views/AdvancedArticleGenerator/types';
import { getTextSpecialSymbols } from '~/modules/tools/views/AiDetectorV4/helpers';
import {
  AiDetectorExecutionType,
  AiDetectorType,
  ExtendAiDetectorExecutionType,
} from '~/modules/tools/views/AiDetectorV4/types';
import {
  AiHumanizerExecutionExtendedType,
  AiHumanizerExecutionType,
  AiHumanizerType,
} from '~/modules/tools/views/AiHumanizerV4/types';
import {
  AiResearcherFormDataType,
  AiResearcherItemType,
} from '~/modules/tools/views/AiResearcherV2/types';
import { FactCheckerExecutionType } from '~/modules/tools/views/FactChecker/types';
import {
  ExtendPlagiarismExecutionType,
  PlagiarismCheckerType,
  PlagiarismExecutionType,
} from '~/modules/tools/views/PlagiarismCheckerV4/types';
import {
  QuestionType,
  QuizGeneratorExecutionType,
} from '~/modules/tools/views/QuizGenerator/types';
import { OneTimeMessageType } from '~/modules/tools/views/ToolsGallery/elements/NotFound/variants/InteractiveNotFound';
import projectConfig, { PROJECT } from '~/projects/config';
import { TOOLS } from '~/projects/sidekicker/constants/tools';
import { CustomAbortController } from '~/services/AbortController';
import analytics from '~/services/analytics';
import errorLogger from '~/services/ErrorLogger';
import AbstractStorage from '~/services/storage/AbstractStorage';
import localStorage from '~/services/storage/localStorage';
import userFieldsService from '~/services/UserFields';
import { MetaType } from '~/types/common';
import { ProductType, REGULARITY } from '~/types/product';
import { ExtraFields, ProfileType } from '~/types/profile';
import {
  CARD_TYPE,
  PAYMENT_TYPE,
  SUBSCRIPTION_STATUS,
  SubscriptionType,
} from '~/types/subscription';
import { TAG } from '~/types/tag';
import { capitalizeCaseToSnakeCase } from '~/utils/text';

import {
  Api,
  CoreReactionContext,
  HandlersBillingHistoryInvoicePresenter,
  HandlersCreateConversionJobRequest,
  HandlersCreateConversionJobResponse,
  HandlersFileConversionFormatResponse,
  HandlersJobStatusResponse,
  HandlersListQuizItem,
  HandlersOrderDataPresenter,
  HandlersUpdateFactCheckRequest,
  ModelsAIDetect,
  ModelsAIHumanizerResponse,
  ModelsArticleOutlineItems,
  ModelsChat,
  ModelsChatEvent,
  ModelsChatMessage,
  ModelsImageGenerationPresenter,
  ModelsPlagiarismCheckerListExecutionsItem,
  ModelsToolCategory,
  ModelsToolExecutionResponse,
  ModelsToolListItem,
  ModelsUser,
  RequestParams,
} from './generated-api/api';

export const ABORT_CHAT_KEY = 'manual_abort_chat_stream';
export const ABORT_TOOL_EXECUTION_KEY = 'manual_abort_tool_execution_stream';

export const ABORT_TOOL_AI_EDIT_KEY = 'manual_abort_tool_ai_edit_stream';

const PAYMENT_SANDBOX_KEY = 'jd-sandbox';

export const REST_API_BASE_URL_LOCALSTORAGE_KEY = 'REST_API_BASE_URL';

export const BASE_API_URL =
  localStorage.getByKey(REST_API_BASE_URL_LOCALSTORAGE_KEY) ||
  process.env.REST_API_BASE_URL ||
  '/';

const baseApiClient = new Api({
  baseUrl: BASE_API_URL,
});

type TrustPilotData = {
  invite_url: string;
  status: 'invited' | 'left-review';
  type: 'url' | 'email';
};

export type BillingHistoryInvoice = Required<
  Pick<
    HandlersBillingHistoryInvoicePresenter,
    'amount' | 'currency' | 'date' | 'invoice_id' | 'order_id'
  >
>;

export type BillingHistoryOrderData = Required<
  Pick<
    HandlersOrderDataPresenter,
    | 'amount'
    | 'charged_at'
    | 'currency'
    | 'customer_email'
    | 'description'
    | 'merchant_address'
    | 'merchant_legal_name'
    | 'merchant_tax_id'
    | 'order_id'
    | 'payment_card_brand'
    | 'payment_card_last4'
  >
>;

class FatalError extends Error {}

export class PlatformApi {
  static ACCESS_TOKEN_STORAGE_KEY = 'access_token';
  static REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

  isInitialized = false;
  accessToken: string | null = null;
  refreshToken: string | null = null;
  private isRefreshingToken = false;
  private refreshPromise: Promise<void> | null = null;

  storage: AbstractStorage;
  private notAuthorizedCallback?: () => void;
  private updateTokensCallbacks: Array<
    (tokens: { accessToken: string; refreshToken: string } | null) => void
  > = [];

  constructor({
    storage,
    notAuthorizedCallback,
  }: {
    storage: AbstractStorage;
    notAuthorizedCallback?: () => void;
  }) {
    this.storage = storage;
    this.notAuthorizedCallback = notAuthorizedCallback;
  }

  private async init(): Promise<void> {
    const [accessToken, refreshToken] = await Promise.all([
      this.storage.getByKey(PlatformApi.ACCESS_TOKEN_STORAGE_KEY),
      this.storage.getByKey(PlatformApi.REFRESH_TOKEN_STORAGE_KEY),
    ]);
    if (accessToken && refreshToken) {
      this.setAccessKeys(accessToken, refreshToken);
    }
    this.isInitialized = true;
  }

  subscribeUpdateTokens(
    callback: (
      tokens: { accessToken: string; refreshToken: string } | null,
    ) => void,
  ): () => void {
    this.updateTokensCallbacks.push(callback);

    return () => {
      this.updateTokensCallbacks = this.updateTokensCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  static async handleError(e: any, methodName: string): Promise<Error> {
    try {
      if (e.message === 'Invalid response') {
        errorLogger.captureException(e, `Error in ${methodName}`);
      }

      if (e.status === 403) {
        if (e instanceof Response) {
          // Unusual response
          const parsedData = await e.json();
          if (parsedData?.error) {
            throw Error(parsedData?.error);
          }
        }
        if (e.error?.error) {
          throw Error(e.error.error);
        }
        throw new ForbiddenError('Forbidden');
      }

      if (e.status === 404) {
        if (e instanceof Response) {
          // for forget password. Unusual response
          const parsedData = await e.json();
          if (parsedData?.error) {
            throw Error(parsedData?.error);
          }
        }
        if (e.error?.error) {
          throw Error(e.error.error);
        }

        throw Error('Not found');
      }

      if (e.status === 429) {
        errorLogger.captureException(e, `Too many requests in ${methodName}`);
      }

      if (e.status >= 500) {
        errorLogger.captureException(e, `Server Error in ${methodName}`);
        throw Error('Server error');
      }

      if (e?.error?.fields) {
        throw new FieldsError(e.error);
      }

      if (e?.error?.error) {
        throw new Error(e.error.error);
      }

      if (e instanceof Response) {
        const parsedData = await e.json();

        if (parsedData.fields) {
          throw new FieldsError(parsedData);
        }

        if (parsedData.error) {
          throw new Error(parsedData.error);
        }
      }

      throw e;
    } catch (e: any) {
      if (e instanceof FieldsError) {
        throw e;
      }

      if (e instanceof StreamVendorError) {
        throw e;
      }

      return new Error(e.message || `Error in ${methodName}`);
    }
  }

  private getOriginOverrideHeaders(): Record<string, string> {
    if (process.env.NODE_ENV === 'production') {
      return {};
    }
    return {
      'X-Origin-Override': projectConfig.project.toLowerCase(),
    };
  }

  private getAuthHeaders(): Record<string, string> {
    if (this.accessToken === null) {
      this.clearAccessKeys();
      throw Error('Not Authorized');
    }
    return {
      'Access-Token': this.accessToken,
    };
  }

  private setAccessKeys(access_token: string, refresh_token: string): string {
    this.accessToken = access_token;
    this.refreshToken = refresh_token;
    this.storage.setByKey(PlatformApi.ACCESS_TOKEN_STORAGE_KEY, access_token);
    this.storage.setByKey(PlatformApi.REFRESH_TOKEN_STORAGE_KEY, refresh_token);
    this.updateTokensCallbacks.forEach((cb) => {
      cb({ accessToken: access_token, refreshToken: refresh_token });
    });
    return access_token;
  }

  private clearAccessKeys(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.storage.removeByKey(PlatformApi.ACCESS_TOKEN_STORAGE_KEY);
    this.storage.removeByKey(PlatformApi.REFRESH_TOKEN_STORAGE_KEY);
    this.updateTokensCallbacks.forEach((cb) => {
      cb(null);
    });
  }

  private async makeAuthorizedRequest<T>(
    requestFunction: (params: RequestParams) => Promise<T>,
    methodName: string,
  ): Promise<T> {
    if (!this.isInitialized) {
      await this.init();
    }
    try {
      try {
        return await requestFunction({
          headers: {
            ...this.getAuthHeaders(),
            ...this.getOriginOverrideHeaders(),
          },
        });
      } catch (e: any) {
        if (e?.status === 401 || e?.message === 'Not Authorized') {
          try {
            await this.updateTokens();
          } catch (e: any) {
            if (e.message === 'Not Authorized') {
              await this.clearAccessKeys();
              this.notAuthorizedCallback && this.notAuthorizedCallback();
            }
          }
          return await requestFunction({
            headers: {
              ...this.getAuthHeaders(),
              ...this.getOriginOverrideHeaders(),
            },
          });
        }
        throw e;
      }
    } catch (e) {
      throw await PlatformApi.handleError(e, methodName);
    }
  }

  private getPaymentSandboxKey(user_id: string): string | null {
    let sandbox: string | null = null;
    try {
      const publicSandboxKey = localStorage.getByKey(PAYMENT_SANDBOX_KEY);
      if (publicSandboxKey) {
        sandbox = decrypt(user_id, publicSandboxKey);
      }
    } catch (error) {
      console.error(error);
    }
    return sandbox;
  }

  async updateTokens(): Promise<void> {
    if (this.isRefreshingToken) {
      return this.refreshPromise!;
    }

    this.isRefreshingToken = true;
    this.refreshPromise = new Promise<void>((resolve, reject) => {
      (async (): Promise<void> => {
        try {
          if (this.refreshToken && this.accessToken) {
            const { data } = await baseApiClient.api.v1AuthRefreshTokenCreate(
              {
                refresh_token: this.refreshToken,
                access_token: this.accessToken,
              },
              {
                headers: {
                  ...this.getOriginOverrideHeaders(),
                },
              },
            );

            const { access_token, refresh_token } = data.data;

            if (access_token !== undefined && refresh_token !== undefined) {
              this.setAccessKeys(access_token, refresh_token);
            }
            resolve();
          } else {
            this.clearAccessKeys();
            reject(new Error('Not Authorized'));
          }
        } catch (e) {
          console.error(e);
          this.clearAccessKeys();
          reject(new Error('Not Authorized'));
        } finally {
          this.isRefreshingToken = false;
          this.refreshPromise = null;
        }
      })();
    });

    return this.refreshPromise;
  }

  async signIn(email: string, password: string): Promise<void> {
    try {
      const { data } = await baseApiClient.api.v1AuthLoginCreate(
        {
          email: email.trim(),
          password,
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
      const { access_token, refresh_token } = data.data;
      if (access_token && refresh_token) {
        this.setAccessKeys(access_token, refresh_token);
      }
    } catch (error) {
      throw await PlatformApi.handleError(error, 'signIn');
    }
  }

  async chromeSignIn(
    email: string,
    password: string,
  ): Promise<{ accessToken: string; refreshToken: string }> {
    try {
      const { data } = await baseApiClient.api.v1AuthLoginCreate(
        {
          email: email.trim(),
          password,
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
      const { access_token, refresh_token } = data.data;
      if (access_token && refresh_token) {
        return {
          accessToken: access_token,
          refreshToken: refresh_token,
        };
      }
      throw new Error('Invalid response');
    } catch (error) {
      throw await PlatformApi.handleError(error, 'signIn');
    }
  }

  async signUp(params: {
    email: string;
    password: string;
    name: string;
    extra_fields?: Partial<ExtraFields>;
  }): Promise<void> {
    try {
      const { data } = await baseApiClient.api.v1AuthSignUpCreate(
        {
          ...params,
          email: params.email.trim(),
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
      const { access_token, refresh_token } = data.data;
      if (access_token && refresh_token) {
        this.setAccessKeys(access_token, refresh_token);
      }
    } catch (error) {
      throw await PlatformApi.handleError(error, 'signUp');
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await baseApiClient.api.v1PasswordForgotCreate(
        {
          email: email.trim(),
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
    } catch (error) {
      throw await PlatformApi.handleError(error, 'forgotPassword');
    }
  }

  async getTrustpilotUrl(): Promise<TrustPilotData> {
    return this.makeAuthorizedRequest<TrustPilotData>(async (params) => {
      try {
        const { data } =
          await baseApiClient.api.v1TrustpilotInviteCreate(params);

        if (!data?.data) {
          throw new Error('Invalid response structure');
        }

        return data.data;
      } catch (error) {
        throw await PlatformApi.handleError(error, 'getTrustpilotUrl');
      }
    }, 'getTrustpilotUrl');
  }

  async resetPassword(token: string, password: string): Promise<void> {
    try {
      await baseApiClient.api.v1PasswordResetCreate(
        {
          token,
          password,
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
    } catch (error) {
      throw await PlatformApi.handleError(error, 'resetPassword');
    }
  }

  async signOut(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      try {
        await baseApiClient.api.v1AuthLogoutCreate(params);
        this.clearAccessKeys();
      } catch (e) {
        throw await PlatformApi.handleError(e, 'signOut');
      }
    }, 'signOut');
  }

  static mapProfileResponse = (profile: ModelsUser): ProfileType => {
    const extraFields = profile.extra_fields as ExtraFields;

    return {
      id: profile.id || '',
      email: profile.email || '',
      name: profile.name || '',
      avatar: profile.avatar_url || '',
      country: profile.country || '',
      response_count_total: profile.response_count_total || 0,
      user_product: PlatformApi.mapProductResponse(profile.user_product || {}),
      user_subscription: PlatformApi.mapSubscriptionResponse(
        profile.user_subscription || {},
      ),
      // @ts-ignore
      user_policy: PlatformApi.mapPolicyResponse(profile.user_policy || {}),
      // @ts-ignore
      user_onboarding: profile.user_onboarding || {},
      is_stripe_user: profile.is_stripe_user ?? false,
      is_onboarded: profile.is_onboarded ?? true,
      created_at: profile.created_at || '',
      updated_at: profile.updated_at || '',
      backend_ab_tests: profile.ab_tests || {},
      user_features: {},
      // @ts-ignore
      personalized_kit_variation: profile.personalized_kit_variation || null,
      // @ts-ignore
      platform_lang: profile?.platform_lang || '',
      extra_fields: {
        redirect_path: extraFields?.redirect_path || null,
        notifications: extraFields?.notifications || {},
        ab_tests: extraFields?.ab_tests || {},
        highlights: extraFields?.highlights || {},
        test_form: extraFields?.test_form || {},
        sign_up_at: extraFields?.sign_up_at || null,
        cancel_sub_reasons: extraFields?.cancel_sub_reasons || {},
        rewards: extraFields?.rewards || {},
        feedbackV3: extraFields?.feedbackV3 || {},
        is_contributor: extraFields?.is_contributor || false,
        pwa_modal: {
          last_showed_at: extraFields?.pwa_modal?.last_showed_at || null,
          is_showed_first_generation:
            extraFields?.pwa_modal?.is_showed_first_generation || false,
          is_showed_fifth_session:
            extraFields?.pwa_modal?.is_showed_fifth_session || false,
        },
        trust_pilot_can_show_after:
          extraFields?.trust_pilot_can_show_after || null,
      },
      interaction_data_count: {
        response: {
          // @ts-ignore
          '-1': profile?.interaction_data_count?.response['-1'] || 0,
          // @ts-ignore
          '1': profile?.interaction_data_count?.response['1'] || 0,
        },
        rate: {
          // @ts-ignore
          '1': profile?.interaction_data_count?.rate['1'] || 0,
          // @ts-ignore
          '2': profile?.interaction_data_count?.rate['2'] || 0,
          // @ts-ignore
          '3': profile?.interaction_data_count?.rate['3'] || 0,
          // @ts-ignore
          '4': profile?.interaction_data_count?.rate['4'] || 0,
          // @ts-ignore
          '5': profile?.interaction_data_count?.rate['5'] || 0,
        },
      },
    };
  };

  static mapProductResponse = (product): ProductType => {
    return {
      created_at: product.created_at || '',
      is_unlimited: product.is_unlimited || false,
      billing_period: product.billing_period || REGULARITY.MONTH,
      currency: product.currency || 'USD',
      has_trial_period: product.has_trial_period || false,
      id: product.id || '',
      is_solid: product.is_solid || false,
      name: product.name || '',
      alternative_non_trial_name: product.alternative_non_trial_name || '',
      solid_name_for_bi: product.solid_name_for_bi || '',
      price: product.price || 0,
      subscription_type: product.subscription_type || '',
      trial_amount: product.trial_amount || 0,
      trial_currency: product.trial_currency || 'USD',
      trial_period: product.trial_period || 0,
      updated_at: product.updated_at || '',
      regularity: product.regularity || REGULARITY.MONTH,
      new_price_ui: product.new_price_ui || 0,
      old_price_ui: product.old_price_ui || 0,
      discount_percentage_ui: product.discount_percentage_ui || 0,
      regularity_ui: product.regularity_ui || REGULARITY.MONTH,
      description_ui: product.description_ui || '',
      details_ui: product.details_ui || '',
      is_popular_ui: product.is_popular_ui || false,
      tags: product.tags || [],
    };
  };

  static mapSubscriptionResponse = (subscription): SubscriptionType => {
    return {
      id: subscription.id ?? '',
      is_unlimited: subscription.is_unlimited || false,
      billing_period: subscription.billing_period || REGULARITY.MONTH,
      product_id: subscription.product_id || '',
      product_tags: subscription.product_tags || [],
      subscription_price: subscription.subscription_price || 0,
      subscription_name: subscription.subscription_name || '',
      status: subscription.status || SUBSCRIPTION_STATUS.ACTIVE,
      started_at: subscription.started_at || '',
      expired_at: subscription.expired_at || null,
      next_charge_at: subscription.next_charge_at || null,
      cancelled_at: subscription.cancelled_at || null,
      is_trial: subscription.is_trial || false,
      cancel_code: subscription.cancel_code || null,
      cancel_message: subscription.cancel_message || null,
      payment_type: subscription.payment_type || PAYMENT_TYPE.CARD,
      card_type: subscription.card_type || CARD_TYPE.NONE,
      pause_from: subscription.pause_from || null,
      pause_to: subscription.pause_to || null,
      created_at: subscription.created_at || '',
      updated_at: subscription.updated_at || '',
      words_generated_total: subscription.words_generated_total || 0,
      saved_time_minutes: subscription.saved_time_minutes || 0,
      response_count_total: subscription.response_count_total || 0,
      plagiarism_found_count: subscription.plagiarism_found_count || 0,
      ai_detected_count: subscription.ai_detected_count || 0,
      ai_detections_bypassed_count:
        subscription.ai_detections_bypassed_count || 0,
      plagiarism_removed_count: subscription.plagiarism_removed_count || 0,
    };
  };

  static mapPolicyResponse = (policy): PolicyType => {
    return {
      features: {
        plagiarism_check: {
          plagiarism_detailed_report:
            policy.features?.plagiarism_check?.plagiarism_detailed_report ??
            true,
          plagiarism_input_limit_overflow:
            policy.features?.plagiarism_check
              ?.plagiarism_input_limit_overflow ?? true,
        },
        ai_content_detector: {
          detector_input_highlights:
            policy.features?.ai_content_detector?.detector_input_highlights ??
            true,
          detector_detailed_report:
            policy.features?.ai_content_detector?.detector_detailed_report ??
            true,
          detector_input_limit_overflow:
            policy.features?.ai_content_detector
              ?.detector_input_limit_overflow ?? true,
        },

        ai_text_humanizer: {
          humanizer_goal:
            policy.features?.ai_text_humanizer?.humanizer_goal ?? true,
        },

        paraphrase_text: {
          paraphraser_level:
            policy.features?.paraphrase_text?.paraphraser_level ?? true,
          paraphraser_tone_of_voice:
            policy.features?.paraphrase_text?.paraphraser_tone_of_voice ?? true,
          paraphraser_output_counter:
            policy.features?.paraphrase_text?.paraphraser_output_counter ??
            true,
        },
      },
    };
  };

  static mapToolExecutionResponse = (
    item: ModelsToolExecutionResponse & { title?: string },
  ): ToolExecutionType & { title: string } => {
    return {
      id: item.id || '',
      title: item.title || '',
      text: item.ai_response || '',
      // TODO! 1455. got incorrect reaction type from swagger
      // @ts-ignore
      reaction: item.user_reaction ?? REACTION.NONE,
      symbols: item.chars_count || 0,
      words: item.words_count || 0,
      ai_edit_items: item.ai_edit_items?.length
        ? item.ai_edit_items.map(
            ({ id, ai_edit_result, user_reaction, ai_model }) => ({
              id: id || '',
              text: ai_edit_result || '',
              reaction: user_reaction || 0,
              ai_model: ai_model || '',
            }),
          )
        : [],
      children:
        item.children && item.children.length
          ? item.children.map((item) =>
              PlatformApi.mapToolExecutionResponse(item),
            )
          : [],
      user_inputs: item.user_inputs || {},
      // @ts-ignore
      ai_model: item.ai_model || '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  };

  static mapPlagiarismExecutionResponse = (
    item: ModelsPlagiarismCheckerListExecutionsItem,
  ): ExtendPlagiarismExecutionType => {
    return {
      export_id: item.export_id || '',
      scan_id: item.scan_id || '',
      aggregated_score: item.aggregated_score || 0,
      identical_words: item.identical_words || 0,
      minor_changed_words: item.minor_changed_words || 0,
      related_meaning_words: item.related_meaning_words || 0,
      omitted_words: item.omitted_words || 0,
      sources_count: item.sources_count || 0,
      document_link: item.document_link || '',
      reaction: item.reaction || 0,
      request_message: item.request_message || '',
      is_contain_quotes: item.is_contain_quotes || false,
      summary: item.summary || '',
      ai_detector_execution: item.detector_result
        ? PlatformApi.mapAiDetectorExecutionResponse(item.detector_result)
        : null,
      // @ts-ignore
      ...PlatformApi.mapDetectorsWordsResponse(item),
      // @ts-ignore
      ai_model: item.ai_model || '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  };

  static mapAIHumanizerExecutionResponse = (
    item: ModelsAIHumanizerResponse,
  ): AiHumanizerExecutionExtendedType => {
    return {
      id: item.execution_id || '',
      mode: item.mode || item.model || '',
      language: item.language || '',
      // @ts-ignore
      mixed_languages: Boolean(item.mixed_languages),
      reaction: item.user_reaction ?? REACTION.NONE,
      summary: item.summary || '',
      request_message: item.request_message || '',
      response_message: item.response_message || '',
      ai_edit_items: item.ai_edits?.length
        ? item.ai_edits.map(
            ({ id, ai_edit_result, user_reaction, ai_model }) => ({
              id: id || '',
              text: ai_edit_result || '',
              reaction: user_reaction || 0,
              ai_model: ai_model || '',
            }),
          )
        : [],
      // @ts-ignore
      ai_detector_execution: item.detector_result
        ? // @ts-ignore
          PlatformApi.mapAiDetectorExecutionResponse(item.detector_result)
        : null,
      // @ts-ignore
      ...PlatformApi.mapDetectorsWordsResponse(item),
      // @ts-ignore
      ai_model: item.ai_model || '',
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || new Date().toISOString(),
    };
  };

  static mapAiResearcherExecutionResponse = (item: {
    id: string;
    task: string;
    reaction: string;
    research: string;
    pdf_link: string;
    ai_model: string;
  }): AiResearcherItemType => {
    return {
      id: item.id || '',
      task: item.task || '',
      output: item.research || '',
      reaction: (item.reaction || REACTION.NONE) as REACTION,
      pdf_link: item.pdf_link || '',
      ai_model: item.ai_model || '',
    };
  };

  static mapFactCheckerExecutionResponse = (item): FactCheckerExecutionType => {
    return {
      id: item.id || '',
      reaction: item.reaction || 0,
      updated_text: item.updated_text || '',
      is_copied: item.is_copied || false,
      fact_checks: item.fact_checks || [],
      ai_model: item.ai_model || '',
      created_at: item.created_at || new Date().toISOString(),
      updated_at: item.updated_at || new Date().toISOString(),
    };
  };

  static mapImageGenerationExecutionResponse = (
    item: ModelsImageGenerationPresenter,
  ): ImageGenerationExecutionItemType => {
    return {
      id: item.id || '',
      instruction: item.instruction || '',
      size: (item.image_size || '1024x1024') as
        | '1024x1024'
        | '1024x1536'
        | '1536x1024',
      hd_quality: item.hd_quality || false,
      style: (item.style || 'vivid') as 'vivid' | 'natural',
      preview_image: item.preview_base64_image || '',
      image_png_src: item.image_png_src || '',
      image_jpeg_src: item.image_jpeg_src || '',
      reaction: (item.reaction || 0) as -1 | 0 | 1,
      // @ts-ignore
      ai_model: item.ai_model || '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  };

  static mapAiDetectorExecutionResponse = (
    item: ModelsAIDetect,
  ): ExtendAiDetectorExecutionType => {
    return {
      id: item.id || '',
      summary: item.summary || '',
      report_url: item.report_url || '',
      fake_percentage: item.fake_percentage_v2 || item.fake_percentage || 0, // TODO! Remove item.fake_percentage as a deprecated for Ai Detector V1
      detected_sentences: item.detected_sentences || [],
      detected_special_symbols:
        getTextSpecialSymbols(item.input_text || '') || [],
      detected_non_latin_languages: item.detected_non_latin_languages || [],
      request_message: item.input_text || '',
      reaction: item.user_reaction || REACTION.NONE,
      // @ts-ignore
      free_humanize_count: item.free_humanize_count
        ? // @ts-ignore
          item.free_humanize_count
        : 0,
      // @ts-ignore
      plagiarism_check_execution: item.plagiarism_check // TODO
        ? PlatformApi.mapPlagiarismExecutionResponse(
            // @ts-ignore
            item.plagiarism_check,
          )
        : null,
      // @ts-ignore
      ...PlatformApi.mapDetectorsWordsResponse(item),
      // @ts-ignore
      ai_model: item.ai_model || '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  };

  static mapQuizGeneratorExecutionResponse = (
    item: HandlersListQuizItem,
  ): QuizGeneratorExecutionType => {
    return {
      execution_id: item.quiz_id || '',
      title: item.title || '',
      quiz:
        item.questions?.map((question_item) => ({
          id: question_item.question_id || '',
          type:
            (question_item.question_type as unknown as QuestionType) ||
            QuestionType.SINGLE,
          question: question_item.question || '',
          options:
            question_item.options?.map((option_item) => ({
              id: option_item.id || '',
              text: option_item.text || '',
              is_correct: option_item.is_correct || false,
              is_selected: option_item.is_selected || false,
            })) || [],
          is_completed: question_item.is_completed || false,
        })) || [],
      reaction: (item.user_reaction as REACTION) || REACTION.NONE,
      // @ts-ignore
      ai_model: item.ai_model || '',
    };
  };

  static mapDetectorsWordsResponse = (item: {
    regular_words_total?: number;
    regular_words_left?: number;
    extra_words_total?: number;
    extra_words_left?: number;
    freemium_words_left?: number;
    freemium_words_total?: number;
  }): {
    words_total: number;
    words_left: number;
    regular_words_total: number;
    regular_words_left: number;
    extra_words_total: number;
    extra_words_left: number;
    freemium_words_left: number;
    freemium_words_total: number;
  } => {
    return {
      words_total:
        (item.regular_words_total || 0) + (item.extra_words_left || 0),
      words_left: (item.regular_words_left || 0) + (item.extra_words_left || 0),
      freemium_words_total: item.freemium_words_total || 0,
      freemium_words_left: item.freemium_words_left || 0,
      regular_words_total: item.regular_words_total || 0,
      regular_words_left: item.regular_words_left || 0,
      extra_words_total: item.extra_words_total || 0,
      extra_words_left: item.extra_words_left || 0,
    };
  };

  static mapArticleExecutionResponse = (item): ArticleExecutionType => {
    return {
      id: item.id || '',
      title: item.title || '',
      text: item.ai_response || '',
      reaction: item.user_reaction ?? REACTION.NONE,
      symbols: item.chars_count || 0,
      words: item.words_count || 0,
      ai_model: item.ai_model || '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  };

  static mapOutlineItemsResponse = (
    responseItem: ModelsArticleOutlineItems,
  ): OutlineItemType => {
    return {
      id: uuidv4(),
      content: responseItem.content || '',
      items: responseItem.items
        ? responseItem.items.map((item) =>
            PlatformApi.mapOutlineItemsResponse(item),
          )
        : [],
    };
  };

  static mapToolCategoriesResponse(item: ModelsToolCategory): ToolCategoryType {
    return {
      id: item.id || '',
      name: item.name || '',
      description: item.description || '',
    };
  }

  static filterMessagesResponse = (
    messages: ModelsChatMessage[],
  ): ModelsChatMessage[] => {
    return messages.filter(
      ({ message_context }) =>
        (message_context as unknown as MESSAGE_CONTEXT) !==
        MESSAGE_CONTEXT.SYSTEM,
    );
  };

  static mapMessageResponse = (
    message: ModelsChatMessage & {
      recommended_tool?: RecommendedChatPromoToolType;
    },
  ): MessageItemType => {
    return {
      id: message.id || '',
      type: MessageType.regular,
      body: {
        response_message: message.ui_response || message.ai_response || '',
        request_message: message.ui_prompt || message.user_prompt || '',
        reaction: (message.user_reaction as REACTION) ?? REACTION.NONE,
        personality: (message.personality ||
          PERSONALITY.GENERAL) as PERSONALITY,
        ...(message.recommended_tool
          ? { recommended_tool: message.recommended_tool }
          : {}),
      },
      ai_model: message.ai_model || '',
      created_at: message.created_at || '',
      updated_at: message.updated_at || '',
    };
  };

  static mapTemplateResponse = (
    tool: ModelsToolListItem & {
      last_inputs: Record<string, string>;
      is_limited?: boolean;
    },
  ): ExtendedToolType => {
    return {
      id: tool.id || '',
      category_id: tool.category_id || '',
      slug: tool.slug || '',
      category_name: tool.category_title || '',
      image_src: tool.image_src || '',
      symbol: tool.emoji || '',
      title: tool.name || '',
      description: tool.description || '',
      path: tool.path || '',
      tags: (tool.tags || []) as TAG[],
      ui_schema: (tool.ui_schema || {}) as Record<string, UiFieldType>,
      validation_schema:
        tool.json_schema && tool.ui_schema
          ? addTrimValidationToScheme(
              tool.json_schema,
              tool.ui_schema as Record<string, UiFieldType>,
            )
          : {},

      color: CardColor.DEFAULT,
      last_inputs: tool.last_inputs || {},
      is_limited: tool.is_limited ?? false,
      ...(projectConfig.project !== PROJECT.JUSTDONE && TOOLS[tool.id || '']
        ? TOOLS[tool.id || '']
        : {}),
    };
  };

  static mapChatResponse = (message: ModelsChat): ChatItemType => {
    return {
      id: message.id || '',
      name: message.name || '',
      created_at: message.created_at || '',
      updated_at: message.updated_at || '',
      chat_type: (message.chat_type || '') as CHAT_TYPE,
      current_personality: (message.current_personality || '') as PERSONALITY,
    };
  };

  async getProfile(): Promise<ProfileType> {
    return this.makeAuthorizedRequest<ProfileType>(async (params) => {
      const { data } = await baseApiClient.api.v1ProfileList(params);

      if (data.data) {
        return PlatformApi.mapProfileResponse(data.data);
      }
      throw Error('Invalid response');
    }, 'getProfile');
  }

  async getExtraAuthTokens(): Promise<{
    access_token: string;
    refresh_token: string;
  }> {
    return this.makeAuthorizedRequest<{
      access_token: string;
      refresh_token: string;
    }>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ChromeExtensionGenerateAccessTokenCreate(
          params,
        );
      if (data?.data?.access_token && data?.data?.refresh_token) {
        return {
          access_token: data.data.access_token,
          refresh_token: data.data.refresh_token,
        };
      }
      throw Error('Invalid response');
    }, 'getExtraAuthTokens');
  }

  async getChatList({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: ChatItemType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: ChatItemType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ChatsList(query, params);

      if (data) {
        const items = data.data;
        const meta = data.meta;
        return {
          data: items
            ? items.map((item) => PlatformApi.mapChatResponse(item))
            : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response in getChatList');
    }, 'getChatList');
  }

  async getMessagesByChatId(
    chatId: string,
    {
      query,
    }: {
      query: {
        page?: number;
        per_page?: number;
        sort_by?: string;
        sort_type?: string;
      };
    },
  ): Promise<{
    data: MessageItemType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: MessageItemType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ChatsMessagesDetail(
        chatId,
        query,
        params,
      );

      if (data) {
        const items = data.data;
        const meta = data.meta;
        return {
          data: items
            ? PlatformApi.filterMessagesResponse(items).map((item) =>
                PlatformApi.mapMessageResponse(item),
              )
            : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response');
    }, 'getMessagesByChatId');
  }

  async sendChatPrompt(
    {
      message,
      message_type,
      chat_id,
      personality,
      ui_message,
      ui_response,
      message_context = MESSAGE_CONTEXT.DEFAULT,
    }: DTOMessageItemType,
    callback: {
      onMessageChunkReceive?: (data: {
        message_id: string;
        chunk: string;
      }) => void;
      onChatCreated: (chat: ChatItemType) => void;
      onMessageCompleteSuccess: (message: MessageItemType) => void;
      onMessageCompleteFail: (message: MessageItemType, error: string) => void;
      onClose?: () => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        if (abortController) {
          abortController.instance.signal.onabort = (e): void => {
            // @ts-ignore
            if (e.currentTarget?.reason === ABORT_CHAT_KEY) {
              callback.onClose && callback.onClose();
            }
          };
        }

        let recommended_tool = null;

        fetchEventSource(`${baseApiClient.baseUrl}/api/v1/chats/events`, {
          method: 'POST',
          openWhenHidden: true,
          signal: abortController?.instance.signal,
          headers: {
            // @ts-ignore
            'Access-Token': params?.headers['Access-Token'] || '',
            ...this.getOriginOverrideHeaders(),
          },
          body: JSON.stringify({
            message,
            message_type,
            chat_id,
            personality,
            message_context,
            ui_prompt: ui_message,
            ui_response,
          }),
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return; // everything's good
            } else if (response.status === 401 || response.status === 403) {
              abortController?.instance.abort();
              abortController?.resetInstance();
              reject(new FatalError('Not Authorized'));
            } else if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              try {
                const body = await response.json();
                throw new FatalError(body.error);
              } catch (e: any) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                reject(new FatalError(e.message || ''));
              }
            }
          },
          onmessage(event) {
            if (!event.data) {
              return;
            }
            const data = JSON.parse(event.data);

            if (!data.event_type) {
              return;
            }

            switch (data.event_type) {
              case ModelsChatEvent.EventChatCreated:
                callback.onChatCreated(
                  PlatformApi.mapChatResponse({
                    ...data.data,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                  }),
                );
                break;
              case ModelsChatEvent.ChatToolPromoReady:
                recommended_tool = data.data;
                break;
              case ModelsChatEvent.EventAIResponseChunk:
                callback.onMessageChunkReceive &&
                  callback.onMessageChunkReceive(data.data);
                break;
              case ModelsChatEvent.EventConversationComplete:
                callback.onMessageCompleteSuccess(
                  PlatformApi.mapMessageResponse({
                    ...data.data,
                    recommended_tool,
                  }),
                );
                break;
              case ModelsChatEvent.EventChatConversationFail:
                callback.onMessageCompleteFail(
                  PlatformApi.mapMessageResponse({
                    ...data.data.data, // TODO! If you see this part of the code – say hello to the backend devs and their interfaces :)
                    recommended_tool,
                  }),
                  data.data.error || 'Error :(',
                );
                break;
            }
          },
          onclose() {
            callback.onClose && callback.onClose();
            resolve();
          },
        });
      });
    }, 'sendChatPrompt');
  }

  async sendOneTimePromptCompleted(prompt: string): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const controller = new AbortController();
      return new Promise((resolve, reject): void => {
        fetchEventSource(`${baseApiClient.baseUrl}/api/v1/prompt/simple`, {
          method: 'POST',
          openWhenHidden: true,
          signal: controller.signal,
          headers: {
            // @ts-ignore
            'Access-Token': params?.headers['Access-Token'] || '',
            ...this.getOriginOverrideHeaders(),
          },
          body: JSON.stringify({
            prompt,
          }),
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return; // everything's good
            } else if (response.status === 401 || response.status === 403) {
              controller.abort();
              reject(new FatalError('Not Authorized'));
            } else if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              try {
                const body = await response.json();
                throw new FatalError(body.error);
              } catch (e: any) {
                controller.abort();
                reject(new FatalError(e.message || ''));
              }
            }
          },

          onmessage(event) {
            if (!event.data) {
              return; // keepalive, just waiting for another event
            }

            const data = JSON.parse(event.data);

            if (data.error || !data.event_type) {
              controller.abort();
              reject(
                new StreamVendorError(data.error || 'no event data event_type'),
              );
            }

            if (
              data.event_type === ModelsChatEvent.EventSimplePropmptComplete
            ) {
              resolve(data.data.ai_response);
              return;
            }

            return '';
          },
          onclose() {
            resolve('');
          },
        });
      });
    }, 'sendOneTimePromptCompleted');
  }

  async sendOneTimePromptChunked(
    prompt: string,
    callback: {
      onMessageChunkReceive?: (data: {
        message_id: string;
        chunk: string;
      }) => void;
      onChatCreated?: (chat: ChatItemType) => void;
      onMessageComplete: (message: OneTimeMessageType) => void;
      onClose?: () => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        if (abortController) {
          abortController.instance.signal.onabort = (e): void => {
            // @ts-ignore
            if (e.currentTarget?.reason === ABORT_CHAT_KEY) {
              callback.onClose && callback.onClose();
            }
          };
        }

        fetchEventSource(`${baseApiClient.baseUrl}/api/v1/prompt/simple`, {
          method: 'POST',
          openWhenHidden: true,
          signal: abortController?.instance.signal,
          headers: {
            // @ts-ignore
            'Access-Token': params?.headers['Access-Token'] || '',
            ...this.getOriginOverrideHeaders(),
          },
          body: JSON.stringify({
            prompt,
          }),
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return; // everything's good
            } else if (response.status === 401 || response.status === 403) {
              abortController?.instance.abort();
              abortController?.resetInstance();
              reject(new FatalError('Not Authorized'));
            } else if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              try {
                const body = await response.json();
                throw new FatalError(body.error);
              } catch (e: any) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                reject(new FatalError(e.message || ''));
              }
            }
          },
          onmessage(event) {
            if (!event.data) {
              return;
            }
            const data = JSON.parse(event.data);

            if (!data.event_type) {
              return;
            }

            switch (data.event_type) {
              case ModelsChatEvent.EventSimplePromptChunk:
                callback.onMessageChunkReceive &&
                  callback.onMessageChunkReceive(data.data);
                break;
              case ModelsChatEvent.EventSimplePropmptComplete:
                callback.onMessageComplete({
                  response_message: data.data.ai_response,
                  request_message: prompt,
                });

                break;
            }
          },
          onclose() {
            callback.onClose && callback.onClose();
            resolve();
          },
        });
      });
    }, 'sendOneTimePromptChunked');
  }

  async getPromptImprover(prompt: string): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const { data } = await baseApiClient.api.v1PromptImproveCreate(
        { prompt },
        params,
      );

      if (data.data) {
        return data.data.improved_prompt;
      }
    }, 'getPromptImprover');
  }

  async sendTestFormData(dataToSend: {
    input: string;
    source_name: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ProfileTestsBrandVoiceCreate(
        dataToSend,
        params,
      );
    }, 'sendTestFormData');
  }

  async createChat(payload: {
    chat_type?: string;
    current_personality?: PERSONALITY;
    chat_name?: string;
    messages?: OneTimeMessageType[];
  }): Promise<ChatItemType> {
    return this.makeAuthorizedRequest<ChatItemType>(async (params) => {
      const { data } = await baseApiClient.api.v1ChatsCreate(
        {
          // @ts-ignore
          chat_type: payload.chat_type,
          // @ts-ignore
          personality: payload.current_personality,
          name: payload.chat_name,
          messages: payload.messages?.map((item) => ({
            ai_response: item.response_message,
            user_prompt: item.request_message,
          })),
        },
        params,
      );

      if (data.data) {
        return data.data;
      }

      throw Error('Invalid response in createChat');
    }, 'createChat');
  }

  async deleteChat(chatId: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ChatsDelete(chatId, params);
    }, 'deleteChat');
  }

  async updateChat(
    chatId: string,
    payload: {
      chat_type?: string;
      current_personality?: PERSONALITY;
      name?: string;
    },
  ): Promise<ChatItemType> {
    return this.makeAuthorizedRequest<ChatItemType>(async (params) => {
      const { data } = await baseApiClient.api.v1ChatsUpdate(
        chatId,
        // @ts-ignore
        payload,
        params,
      );

      if (data.data) {
        return data.data;
      }
    }, 'updateChat');
  }

  async getGroupedTools(): Promise<GroupToolsType[]> {
    return this.makeAuthorizedRequest<GroupToolsType[]>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsList(params);

      if (data.data) {
        return data.data.map((group) => ({
          group_name: group.name || '',
          group_id: capitalizeCaseToSnakeCase(group.name.toLowerCase()),
          items: group.items.map((tool) =>
            PlatformApi.mapTemplateResponse(tool),
          ),
        }));
      }
      throw Error('Invalid response in getGroupedTools');
    }, 'getGroupedTools');
  }

  async getToolCategories({
    query,
  }: {
    query: {
      sort_type?: SORT_ORDER;
      sort_by?: string;
      page: number;
      per_page: number;
    };
  }): Promise<{
    data: ToolCategoryType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: ToolCategoryType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolCategoriesList(
        query,
        params,
      );

      if (data.data && data.meta) {
        const categories = data.data;
        const meta = data.meta;
        return {
          data: categories.map((item) =>
            PlatformApi.mapToolCategoriesResponse(item),
          ),
          meta: {
            currentPage: meta.current_page ?? 1,
            pageSize: meta.page_size ?? 10,
            totalCount: meta.total_count ?? 0,
            totalPages: meta.total_pages ?? 1,
          },
        };
      }
      throw Error('Invalid response');
    }, 'getToolCategories');
  }

  async getTools({
    query,
  }: {
    query: {
      category_ids?: string;
      tags?: string;
      search_fields?: string;
      search_text?: string;
      sort_type?: SORT_ORDER;
      sort_by?: string;
      page: number;
      per_page: number;
    };
  }): Promise<{
    data: ToolType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: ToolType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsByFiltersList(
        query,
        params,
      );

      if (data.meta) {
        const tools = data.data || [];
        const meta = data.meta;

        return {
          data: tools.map((item) => PlatformApi.mapTemplateResponse(item)),
          meta: {
            currentPage: meta.current_page ?? 1,
            pageSize: meta.page_size ?? 10,
            totalCount: meta.total_count ?? 0,
            totalPages: meta.total_pages ?? 1,
          },
        };
      }
      throw Error('Invalid response in getTools');
    }, 'getTools');
  }

  async getFileConverterFormats(query?: {
    inputFormat?: string | null;
    outputFormat?: string | null;
  }): Promise<HandlersFileConversionFormatResponse[]> {
    return this.makeAuthorizedRequest<HandlersFileConversionFormatResponse[]>(
      async (params) => {
        const { data } = await baseApiClient.api.v1FileConverterFormatsList(
          {
            ...(query?.inputFormat ? { input_format: query.inputFormat } : {}),
            ...(query?.outputFormat
              ? { output_format: query.outputFormat }
              : {}),
          },
          params,
        );

        if (data?.data) {
          return data.data;
        }

        return [];
      },
      'getPdfConverterFormats',
    );
  }

  async createFileConverterJob(
    body: HandlersCreateConversionJobRequest,
  ): Promise<HandlersCreateConversionJobResponse> {
    return this.makeAuthorizedRequest<HandlersCreateConversionJobResponse>(
      async (params) => {
        const { data } = await baseApiClient.api.v1FileConverterJobsCreate(
          body,
          params,
        );

        if (data?.data) {
          return data.data;
        }

        throw Error('Invalid response');
      },
      'createPdfConverterJob',
    );
  }

  async getFileConverterJobStatus(
    jobId: string,
  ): Promise<HandlersJobStatusResponse> {
    return this.makeAuthorizedRequest<HandlersJobStatusResponse>(
      async (params) => {
        const { data } =
          await baseApiClient.api.v1FileConverterJobsStatusDetail(
            jobId,
            params,
          );

        if (data?.data) {
          return data.data;
        }

        throw Error('Invalid response');
      },
      'getPdfConverterJobStatus',
    );
  }

  async getPersonalizedTools(): Promise<{ items: ExtendedToolType[] }> {
    return this.makeAuthorizedRequest<{ items: ExtendedToolType[] }>(
      async (params) => {
        const data = await baseApiClient.api.v2HomeToolkitList(params);
        const parsedData = await data.json();
        if (parsedData.data) {
          return {
            // @ts-ignore
            items: parsedData.data.map((item) =>
              PlatformApi.mapTemplateResponse(item),
            ),
          };
        }
        throw Error('Invalid response');
      },
      'getTools',
    );
  }

  async getToolById(id: string): Promise<ExtendedToolType> {
    return this.makeAuthorizedRequest<ExtendedToolType>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsDetail(id, params);
      return PlatformApi.mapTemplateResponse(data.data);
    }, 'getToolById');
  }

  async regenerateOutlineSection(
    topic: string,
    items: OutlineItemType[],
    regenerateIndex: number,
  ): Promise<{
    words: number;
    items: OutlineItemType[];
  }> {
    return this.makeAuthorizedRequest<{
      words: number;
      items: OutlineItemType[];
    }>(async (params) => {
      return new Promise((resolve, reject): void => {
        const controller = new AbortController();
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/tools/advanced_article_generator/regenerate-outline-section`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: controller.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify({
              outline: {
                topic,
                items,
              },
              regenerate_index: regenerateIndex,
            }),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                controller.abort();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  throw new FatalError(body.error);
                } catch (e: any) {
                  controller.abort();
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onmessage(event) {
              if (!event.data) {
                return; // keepalive, just waiting for another event
              }

              const data = JSON.parse(event.data);

              if (data.error || !data.event_type) {
                controller.abort();
                reject(
                  new StreamVendorError(
                    data.error || 'no event data event_type',
                  ),
                );
              }

              switch (data.event_type) {
                case ModelsChatEvent.EventAGAOutlineReady:
                  resolve({
                    words: data?.data.word_count,
                    items: data?.data.items.map(
                      PlatformApi.mapOutlineItemsResponse,
                    ),
                  });
                  break;
              }
            },
          },
        );
      });
    }, 'regenerateOutlineSection');
  }

  async getOutlineByTopic(topic: string): Promise<ArticleOutlineType> {
    return this.makeAuthorizedRequest<ArticleOutlineType>(async (params) => {
      return new Promise((resolve, reject): void => {
        const controller = new AbortController();
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/tools/advanced_article_generator/generate-outline`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: controller.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify({ topic }),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                controller.abort();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  throw new FatalError(body.error);
                } catch (e: any) {
                  controller.abort();
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onmessage(event) {
              if (!event.data) {
                return; // keepalive, just waiting for another event
              }

              const data = JSON.parse(event.data);

              if (data.error || !data.event_type) {
                controller.abort();
                reject(
                  new StreamVendorError(
                    data.error || 'no event data event_type',
                  ),
                );
              }

              switch (data.event_type) {
                case ModelsChatEvent.EventAGAOutlineReady:
                  resolve({
                    topic: data?.data.topic,
                    words: data?.data.word_count,
                    items: data?.data.items.map(
                      PlatformApi.mapOutlineItemsResponse,
                    ),
                  });
                  break;
              }
            },
          },
        );
      });
    }, 'getOutlineByTopic');
  }

  async getArticleByOutline(
    topic: string,
    items: OutlineItemType[],
  ): Promise<ArticleExecutionType> {
    return this.makeAuthorizedRequest<ToolExecutionType & { title: string }>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v1/tools/advanced_article_generator/generate-article`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify({ outline: { topic, items } }),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  );
                }

                switch (data.event_type) {
                  case ModelsChatEvent.EventToolExecutionComplete:
                    resolve(
                      PlatformApi.mapToolExecutionResponse(
                        data.data.execution_results[0],
                      ),
                    );
                    break;
                }
              },
            },
          );
        });
      },
      'getArticleByOutline',
    );
  }

  async getLastArticleResults(): Promise<ArticleType> {
    return this.makeAuthorizedRequest(async (params) => {
      const { data } =
        await baseApiClient.api.v1ToolsAdvancedArticleGeneratorLastResultsList(
          params,
        );

      if (data.data) {
        return {
          content: data.data.content.execution_results
            ? PlatformApi.mapArticleExecutionResponse(
                data.data.content.execution_results[0],
              )
            : null,
          topic: data.data.topic,
          outline: data.data.outline.items
            ? {
                ...data.data.outline,
                items: data.data.outline.items.map(
                  PlatformApi.mapOutlineItemsResponse,
                ),
              }
            : null,
        };
      }

      if (data.data === null) {
        return data.data;
      }

      throw Error('Invalid response in getLastArticleResults');
    }, 'getLastArticleResults');
  }

  async updateArticleOutline(outline: ArticleOutlineType): Promise<void> {
    return this.makeAuthorizedRequest(async (params) => {
      const response =
        await baseApiClient.api.v1ToolsAdvancedArticleGeneratorUpdateOutlineCreate(
          { outline },
          params,
        );

      if (response.status !== 200) {
        throw Error('Invalid response in updateArticleOutline');
      }
    }, 'updateArticleOutline');
  }

  async getToolBySlug(slug: string): Promise<ExtendedToolType> {
    return this.makeAuthorizedRequest<ExtendedToolType>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsSlugDetail(slug, params);
      if (data.data) {
        if (projectConfig.checkIsToolAvailable(data.data.id)) {
          return PlatformApi.mapTemplateResponse(data.data);
        }
        throw Error('Permission denied');
      }
      throw Error('Invalid response in getToolBySlug');
    }, 'getToolBySlug');
  }

  async updateToolExecution(
    resultId: string,
    toolId: string,
    dataToUpdate: {
      text: string;
    },
  ): Promise<ToolExecutionType> {
    return this.makeAuthorizedRequest<ToolExecutionType>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsResultsUpdate(
        resultId,
        toolId,
        { ai_response: dataToUpdate.text },
        params,
      );

      if (data.data) {
        return PlatformApi.mapToolExecutionResponse(data.data);
      }
      throw Error('Invalid response');
    }, 'updateToolExecution');
  }

  async deleteToolExecution(toolId: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const response = await baseApiClient.api.v1ToolsExecutionsDelete(
        toolId,
        params,
      );

      if (response.status !== 200) {
        throw Error('Invalid response');
      }
    }, 'deleteToolExecution');
  }

  async sendToolFormData(
    messageData: {
      tool_id: string;
      form_data: Record<string, any>;
    },
    callback: {
      onComplete: (messages: ToolExecutionType[]) => void;
      onCreate?: (messages: ToolExecutionType[]) => void;
      onChunkReceive?: (data: { text: string; id: string }) => void;
      onClose?: () => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        if (abortController) {
          abortController.instance.signal.onabort = (e): void => {
            // @ts-ignore
            if (e.currentTarget?.reason === ABORT_TOOL_EXECUTION_KEY) {
              callback.onClose && callback.onClose();
              resolve();
            }
          };
        }
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/tools/${messageData.tool_id}/execute/stream/chunked`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: abortController?.instance.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify(messageData.form_data),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  throw new FatalError(body.error);
                } catch (e: any) {
                  abortController?.instance.abort();
                  abortController?.resetInstance();
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onclose() {
              resolve();
            },
            onmessage(event) {
              if (!event.data) {
                return;
              }
              const data = JSON.parse(event.data);
              if (data.error) {
                abortController?.instance.abort();
                reject(
                  new StreamVendorError(
                    data.error || 'no event data event_type',
                  ),
                );
              }
              if (!data.event_type) {
                return;
              }

              switch (data.event_type) {
                case ModelsChatEvent.EventToolExecutionCreated:
                  callback.onCreate &&
                    callback.onCreate(
                      data?.data.execution_results.map((item) =>
                        PlatformApi.mapToolExecutionResponse(item),
                      ),
                    );
                  break;
                case ModelsChatEvent.EventToolExecutionChunk:
                  callback.onChunkReceive &&
                    callback.onChunkReceive({
                      id: data?.data.tool_execution_response_id || '',
                      text: data?.data.chunk || '',
                    });
                  break;
                case ModelsChatEvent.EventToolExecutionComplete:
                  callback.onComplete(
                    data?.data.execution_results.map((item) =>
                      PlatformApi.mapToolExecutionResponse({
                        ...item,
                        ai_model: data?.data.ai_model || '',
                      }),
                    ),
                  );
                  break;
              }
            },
          },
        );
      });
    }, 'sendToolFormData');
  }

  async generateToolItemAiEdit(
    toolId: string,
    itemId: string,
    action: string,
    callback: {
      onComplete: (item: AiEditType) => void;
      onChunkReceive?: (item: AiEditType) => void;
      onClose?: () => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        if (abortController) {
          abortController.instance.signal.onabort = (e): void => {
            // @ts-ignore
            if (e.currentTarget?.reason === ABORT_TOOL_AI_EDIT_KEY) {
              callback.onClose && callback.onClose();
              resolve();
            }
          };
        }
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/tools/${toolId}/results/${itemId}/ai_edit`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: abortController?.instance.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify({ action }),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  throw new FatalError(body.error);
                } catch (e: any) {
                  abortController?.instance.abort();
                  abortController?.resetInstance();
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onclose() {
              resolve();
            },
            onmessage(event) {
              if (!event.data) {
                return;
              }
              const data = JSON.parse(event.data);
              if (!data.event_type) {
                return;
              }

              switch (data.event_type) {
                case ModelsChatEvent.AiEditResponseChunkReady:
                  callback.onChunkReceive &&
                    callback.onChunkReceive({
                      text: data?.data.chunk || '',
                      id: data?.data.ai_edit_id || '',
                      reaction: data.data.user_reaction || 0,
                      ai_model: data.ai_model || '',
                    });
                  break;
                case ModelsChatEvent.AiEditResponse:
                  callback.onComplete({
                    id: data.data.id || '',
                    text: data.data.ai_edit_result || '',
                    reaction: data.data.user_reaction || '',
                    ai_model: data.data.ai_model || '',
                  });
                  break;
              }
            },
          },
        );
      });
    }, 'generateToolItemAiEdit');
  }

  async getToolExecutionsByToolId(
    toolId: string,
    {
      query,
    }: {
      query: {
        page?: number;
        per_page?: number;
        sort_by?: string;
        sort_type?: string;
      };
    },
  ): Promise<{
    data: ToolExecutionType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: ToolExecutionType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsExecutionsDetail(
        toolId,
        query,
        params,
      );

      if (data) {
        const items = data.data;
        const meta = data.meta;
        return {
          data:
            items && items.length
              ? items.map((item) => PlatformApi.mapToolExecutionResponse(item))
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response');
    }, 'getToolExecutionsByToolId');
  }

  async getImprovedImageGenerationPrompt(prompt: string): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const data =
        await baseApiClient.api.v1ToolsImageGeneratorImprovePromptCreate(
          { instruction: prompt },
          params,
        );
      if (data.data.data) {
        return data.data.data.improved_prompt || '';
      }
    }, 'getImprovedImageGenerationPrompt');
  }

  async setImageGenerationReaction(
    itemId: string,
    reaction: REACTION,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsImageGeneratorGenerationsReactionCreate(
        itemId,
        // @ts-ignore
        { reaction },
        params,
      );
    }, 'setImageGenerationReaction');
  }

  async getImageGeneratedItems({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: ImageGenerationExecutionItemType[];
    meta: MetaDataResponseType;
  }> {
    return this.makeAuthorizedRequest<{
      data: ImageGenerationExecutionItemType[];
      meta: MetaDataResponseType;
    }>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ToolsImageGeneratorGenerationsList(
          query,
          params,
        );

      if (data) {
        const items = data.data;
        const meta = data.meta;
        return {
          data:
            items && items.length
              ? items.map((item) =>
                  PlatformApi.mapImageGenerationExecutionResponse(item),
                )
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response');
      // throw Error('Invalid response in getImageGeneratedItems');
    }, 'getImageGeneratedItems');
  }

  async getImageGeneratorTool(): Promise<ExtendedToolType> {
    return this.makeAuthorizedRequest<ExtendedToolType>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ToolsImageGeneratorList(params);
      if (data.data) {
        return PlatformApi.mapTemplateResponse(data.data);
      }
      throw Error('Invalid response');
    }, 'getImageGeneratorTool');
  }

  async sendImageGenerationForm(
    formData: ImageGenerationFormDataType,
    callback: {
      onComplete: (message: ImageGenerationExecutionItemType) => void;
      onClose?: () => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        if (abortController) {
          abortController.instance.signal.onabort = (e): void => {
            // @ts-ignore
            if (e.currentTarget?.reason === ABORT_TOOL_EXECUTION_KEY) {
              callback.onClose && callback.onClose();
              resolve();
            }
          };
        }
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/tools/image-generator/generate`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: abortController?.instance.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify({
              hd_quality: formData.hd_quality,
              image_size: formData.size,
              instruction: formData.instruction,
              style: formData.style,
            }),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  if (body.fields) {
                    throw new FieldsError(body);
                  }
                  throw new FatalError(body.error);
                } catch (e: any) {
                  abortController?.instance.abort();
                  abortController?.resetInstance();
                  if (e instanceof FieldsError) {
                    reject(e);
                  }
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onclose() {
              resolve();
            },
            onmessage(event) {
              if (!event.data) {
                return;
              }
              const data = JSON.parse(event.data);
              if (data.error) {
                reject(new FatalError(data.error));
                return;
              }

              if (!data.event_type) {
                return;
              }

              switch (data.event_type) {
                case ModelsChatEvent.ImageGenerationComplete:
                  callback.onComplete(
                    PlatformApi.mapImageGenerationExecutionResponse(data.data),
                  );
                  break;
              }
            },
          },
        );
      });
    }, 'sendImageGenerationForm');
  }

  async getPlagiarismCheckerFile(id: string): Promise<any> {
    return this.makeAuthorizedRequest<any>(async (params) => {
      const data = await baseApiClient.api.v1CopyleaksExportsPdfDetail(
        id,
        params,
      );

      if (data.ok) {
        return data;
      }

      throw Error('Error during file download');
    }, 'getPlagiarismCheckerFile');
  }

  async sendCopyLeaksReaction(id: string, reaction: REACTION): Promise<any> {
    return this.makeAuthorizedRequest<any>(async (params) => {
      const data = await baseApiClient.api.v1CopyleaksExportsReactionCreate(
        id,
        { reaction },
        params,
      );

      if (!data.ok) {
        throw Error('Error during send reaction');
      }
    }, 'sendCopyLeaksReaction');
  }

  async sendZeroGPTReaction(id: string, reaction: REACTION): Promise<any> {
    return this.makeAuthorizedRequest<any>(async (params) => {
      const data =
        await baseApiClient.api.v1ZerogptAiDetectorExecutionsReactionCreate(
          id,
          // @ts-ignore
          { reaction },
          params,
        );

      if (!data.ok) {
        throw Error('Error during send reaction');
      }
    }, 'sendZeroGPTReaction');
  }

  async clearImageGenerationHistory(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsImageGeneratorGenerationsDelete(params);
    }, 'clearImageGenerationHistory');
  }

  async sendAiHumanizerReaction(id: string, reaction: REACTION): Promise<any> {
    return this.makeAuthorizedRequest<any>(async (params) => {
      const data =
        await baseApiClient.api.v1AiHumanizerExecutionsReactionCreate(
          id,
          { reaction },
          params,
        );

      if (!data.ok) {
        throw Error('Error during send reaction');
      }
    }, 'sendAiHumanizerReaction');
  }

  async getAiHumanizerExecutions({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: AiHumanizerExecutionType[];
    meta: MetaType;
  }> {
    return this.makeAuthorizedRequest<{
      data: AiHumanizerExecutionType[];
      meta: MetaType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1AiHumanizerExecutionsList(
        query,
        params,
      );

      if (data) {
        const items: any = data.data;
        const meta = data.meta;

        return {
          data:
            items && items.length
              ? items.map((item) => {
                  return PlatformApi.mapAIHumanizerExecutionResponse(item);
                })
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response in getAiHumanizerExecutions');
    }, 'getAiHumanizerExecutions');
  }

  async sendAiResearcherData(
    data: {
      report_type: string;
      task: string;
      tone: string;
    },
    callback: {
      onLogChunkReceive: (data: {
        content: string;
        metadata: string;
        output: string;
      }) => void;
      onReportChunkReceive: (data: { output: string }) => void;
      onComplete: (data: AiResearcherItemType) => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        fetchEventSource(`${baseApiClient.baseUrl}/api/v2/ai-researcher`, {
          method: 'POST',
          openWhenHidden: true,
          signal: abortController?.instance.signal,
          headers: {
            // @ts-ignore
            'Access-Token': params?.headers['Access-Token'] || '',
            ...this.getOriginOverrideHeaders(),
          },
          body: JSON.stringify(data),
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return; // everything's good
            } else if (response.status === 401 || response.status === 403) {
              abortController?.instance.abort();
              abortController?.resetInstance();
              reject(new FatalError('Not Authorized'));
            } else if (response.status >= 400 && response.status < 500) {
              try {
                const body = await response.json();
                if (body.fields) {
                  throw new FieldsError(body);
                }
                throw new FatalError(body.error);
              } catch (e: any) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                if (e instanceof FieldsError) {
                  reject(e);
                }
                reject(new FatalError(e.message || ''));
              }
            }
          },
          onmessage(event) {
            if (!event.data) {
              return;
            }
            const data = JSON.parse(event.data);

            if (data.error || !data.event_type) {
              abortController?.instance.abort();
              reject(
                new StreamVendorError(data.error || 'no event data event_type'),
              );
            }

            if (data.event_type === 'ai_research_completed') {
              callback.onComplete(
                PlatformApi.mapAiResearcherExecutionResponse(data.data),
              );
            }

            if (
              !data.event_type ||
              !['ai_research_chunk'].includes(data.event_type) ||
              !data.data?.type
            ) {
              return;
            }

            switch (data.data.type) {
              case 'logs':
                callback.onLogChunkReceive({
                  content: data.data?.content || '',
                  metadata: data.data?.metadata || '',
                  output: data.data?.output || '',
                });
                break;
              case 'report':
                callback.onReportChunkReceive({
                  output: data.data?.output || '',
                });
                break;
            }
          },
          onclose() {
            resolve();
          },
        });
      });
    }, 'sendAiResearcherData');
  }

  async sendAiResearcherDataV3(
    data: AiResearcherFormDataType,
    callback: {
      onComplete: (data: AiResearcherItemType) => void;
    },
    abortController?: CustomAbortController,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        fetchEventSource(`${baseApiClient.baseUrl}/api/v3/ai-researcher`, {
          method: 'POST',
          openWhenHidden: true,
          signal: abortController?.instance.signal,
          headers: {
            // @ts-ignore
            'Access-Token': params?.headers['Access-Token'] || '',
            ...this.getOriginOverrideHeaders(),
          },
          body: JSON.stringify(data),
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              return; // everything's good
            } else if (response.status === 401 || response.status === 403) {
              abortController?.instance.abort();
              abortController?.resetInstance();
              reject(new FatalError('Not Authorized'));
            } else if (response.status >= 400 && response.status < 500) {
              try {
                const body = await response.json();
                if (body.fields) {
                  throw new FieldsError(body);
                }
                throw new FatalError(body.error);
              } catch (e: any) {
                abortController?.instance.abort();
                abortController?.resetInstance();
                if (e instanceof FieldsError) {
                  reject(e);
                }
                reject(new FatalError(e.message || ''));
              }
            }
          },
          onclose() {
            resolve();
          },
          onmessage(event) {
            if (!event.data) {
              return;
            }
            const data = JSON.parse(event.data);

            if (data.error || !data.event_type) {
              abortController?.instance.abort();
              reject(
                new StreamVendorError(data.error || 'no event data event_type'),
              );
            }

            if (data.event_type === 'ai_research_completed') {
              callback.onComplete(
                PlatformApi.mapAiResearcherExecutionResponse(data.data),
              );
            }
          },
        });
      });
    }, 'sendAiResearcherData');
  }

  async updateAiHumanizerExecution(
    resultId: string,
    dataToUpdate: {
      ai_response: string;
    },
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1AiHumanizerExecutionsUpdate(
        resultId,
        dataToUpdate,
        params,
      );
    }, 'updateAiHumanizerExecution');
  }

  async deleteAiHumanizerExecutions(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const response =
        await baseApiClient.api.v1AiHumanizerExecutionsDelete(params);

      if (response.status !== 200) {
        throw Error('Invalid response');
      }
    }, 'deleteAiHumanizerExecutions');
  }

  async sendAiHumanizerDataV2(
    data: {
      original_text: string;
      mode: string;
      // ai_detector_check_enabled?: boolean;
    },
    is_freemium_request?: boolean,
  ): Promise<AiHumanizerExecutionExtendedType> {
    return this.makeAuthorizedRequest<AiHumanizerExecutionExtendedType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v2/ai-humanizer${is_freemium_request ? '?is_freemium_request=true' : ''}`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify(data),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  ); // here we use StreamVendorError that is handled at tool
                }

                switch (data.event_type) {
                  case ModelsChatEvent.EventAIHumanizerProcessingComplete:
                    resolve({
                      ...PlatformApi.mapAIHumanizerExecutionResponse(
                        data?.data || {},
                      ),
                    });
                    break;
                }
              },
            },
          );
        });
      },
      'sendAiHumanizerDataV2',
    );
  }

  async getAiHumanizer(): Promise<AiHumanizerType> {
    return this.makeAuthorizedRequest<AiHumanizerType>(async (params) => {
      const { data } = await baseApiClient.api.v1AiHumanizerList(params);

      if (data.data) {
        const tool = data.data;

        return {
          id: tool.id || '',
          title: tool.title || '',
          last_inputs: tool.last_inputs
            ? ({
                original_text: tool.last_inputs.original_text || '',
                mode:
                  tool.last_inputs.mode === 'undefined'
                    ? tool.last_inputs.model || ''
                    : tool.last_inputs.mode || '',
              } as Record<string, string>)
            : {},
          is_limited: tool.is_limited ?? false,
          ...PlatformApi.mapDetectorsWordsResponse(tool),
        };
      }

      throw Error('Invalid response in getAiHumanizer');
    }, 'getAiHumanizer');
  }

  async getAiResearcherExecutions({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: AiResearcherItemType[];
    meta: MetaType;
  }> {
    return this.makeAuthorizedRequest<{
      data: AiResearcherItemType[];
      meta: MetaType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v2AiResearcherExecutionsList(
        query,
        params,
      );

      if (data) {
        const items: any = data.data;
        const meta = data.meta;

        return {
          data:
            items && items.length
              ? items.map((item) => {
                  return PlatformApi.mapAiResearcherExecutionResponse(item);
                })
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response in getAiHumanizerExecutions');
    }, 'getAiResearcherExecutions');
  }

  async sendAiResearcherReaction(id: string, reaction: REACTION): Promise<any> {
    return this.makeAuthorizedRequest<any>(async (params) => {
      const data =
        await baseApiClient.api.v2AiResearcherExecutionsReactionCreate(
          id,
          { reaction },
          params,
        );
      if (!data.ok) {
        throw Error('Error during send reaction');
      }
    }, 'sendAiResearcherReaction');
  }

  async getPlagiarismCheckerExecutions({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: PlagiarismExecutionType[];
    meta: MetaType;
  }> {
    return this.makeAuthorizedRequest<{
      data: PlagiarismExecutionType[];
      meta: MetaType;
    }>(async (params) => {
      const { data } =
        await baseApiClient.api.v1CopyleaksPlagiarismCheckExecutionsList(
          query,
          params,
        );

      if (data) {
        const items: any = data.data;
        const meta = data.meta;
        return {
          data:
            items && items.length
              ? items.map(PlatformApi.mapPlagiarismExecutionResponse)
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response in getPlagiarismCheckerExecutions');
    }, 'getPlagiarismCheckerExecutions');
  }

  async deletePlagiarismCheckerExecutions(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const response =
        await baseApiClient.api.v1CopyleaksPlagiarismCheckExecutionsDelete(
          params,
        );

      if (response.status !== 200) {
        throw Error('Invalid response');
      }
    }, 'deletePlagiarismCheckerExecutions');
  }

  async sendPlagiarismCheckerDataV2(
    data: {
      text: string;
      simplified_scan: boolean;
      // ai_detector_check_enabled?: boolean;
    },
    is_sandbox?: boolean,
  ): Promise<ExtendPlagiarismExecutionType> {
    return this.makeAuthorizedRequest<ExtendPlagiarismExecutionType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v2/copyleaks/plagiarism-check`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify({
                text: data.text,
                simplified_scan: data.simplified_scan,
                // ai_detector_check_enabled: data.ai_detector_check_enabled,
                is_sandbox,
              }),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  );
                }

                switch (data.event_type) {
                  case ModelsChatEvent.EventToolExecutionComplete:
                    resolve(
                      PlatformApi.mapPlagiarismExecutionResponse(data.data),
                    );
                    break;
                }
              },
            },
          );
        });
      },
      'sendPlagiarismCheckerDataV2',
    );
  }

  // TODO! 1386. Didn't get last inputs correctly
  async getPlagiarismChecker(): Promise<PlagiarismCheckerType> {
    return this.makeAuthorizedRequest<PlagiarismCheckerType>(async (params) => {
      const { data } =
        await baseApiClient.api.v1CopyleaksPlagiarismCheckList(params);

      if (data.data) {
        const item = data.data;
        return {
          id: item.id || '',
          title: item.title || '',
          last_inputs: {
            ...item.last_inputs,
            text: item.last_inputs.file_content || '',
          },
          is_limited: item.is_limited ?? false,
          ...PlatformApi.mapDetectorsWordsResponse(item),
        };
      }

      throw Error('Invalid response in getPlagiarismChecker');
    }, 'getPlagiarismChecker');
  }

  async sendPlagiarismReportEmail(id: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1CopyleaksExportsPdfEmailCreate(id, params);
    }, 'sendPlagiarismReportEmail');
  }

  async getFactCheckerTool(): Promise<ExtendedToolType> {
    return this.makeAuthorizedRequest<ExtendedToolType>(async (params) => {
      const { data } = await baseApiClient.api.v2FactCheckerList(params);
      if (data.data) {
        return PlatformApi.mapTemplateResponse(data.data);
      }
      throw Error('Invalid response');
    }, 'getFactCheckerTool');
  }

  async sendFactCheckerData(data: {
    text: string;
  }): Promise<FactCheckerExecutionType> {
    return this.makeAuthorizedRequest<FactCheckerExecutionType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(`${baseApiClient.baseUrl}/api/v2/fact-checker`, {
            method: 'POST',
            openWhenHidden: true,
            signal: controller.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },
            body: JSON.stringify({
              text: data.text,
            }),
            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                controller.abort();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  if (body.fields) {
                    throw new FieldsError(body);
                  }
                  throw new FatalError(body.error);
                } catch (e: any) {
                  controller.abort();
                  if (e instanceof FieldsError) {
                    reject(e);
                  }
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onmessage(event) {
              if (!event.data) {
                return; // keepalive, just waiting for another event
              }

              const data = JSON.parse(event.data);

              if (data.error || !data.event_type) {
                controller.abort();
                reject(
                  new StreamVendorError(
                    data.error || 'no event data event_type',
                  ),
                );
              }

              switch (data.event_type) {
                case ModelsChatEvent.FactCheckComplete:
                  resolve(
                    PlatformApi.mapFactCheckerExecutionResponse(data.data),
                  );
                  break;
              }
            },
          });
        });
      },
      'sendFactCheckerData',
    );
  }

  async updateFactCheckerExecution(
    id: string,
    body: HandlersUpdateFactCheckRequest,
  ): Promise<FactCheckerExecutionType> {
    return this.makeAuthorizedRequest<FactCheckerExecutionType>(
      async (params) => {
        const { data } = await baseApiClient.api.v2FactCheckerPartialUpdate(
          id,
          body,
          params,
        );

        if (data.data) {
          return data.data;
        }

        throw Error('Invalid response');
      },
      'updateFactCheckerExecution',
    );
  }

  async getAiDetectorExecutions({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: AiDetectorExecutionType[];
    meta: MetaType;
  }> {
    return this.makeAuthorizedRequest<{
      data: AiDetectorExecutionType[];
      meta: MetaType;
    }>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ZerogptAiDetectorExecutionsList(
          query,
          params,
        );

      if (data) {
        const items: any = data.data;
        const meta = data.meta;
        return {
          data:
            items && items.length
              ? items.map(PlatformApi.mapAiDetectorExecutionResponse)
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response in getAiDetectorExecutions');
    }, 'getAiDetectorExecutions');
  }

  async regenerateQuizItem(data: {
    executionId: string;
  }): Promise<QuizGeneratorExecutionType> {
    return this.makeAuthorizedRequest<QuizGeneratorExecutionType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v1/quiz-generator/quizzes/${data.executionId}/regenerate`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  );
                }

                switch (data.event_type) {
                  case 'quiz_generate_complete':
                    resolve(
                      PlatformApi.mapQuizGeneratorExecutionResponse(data.data),
                    );
                    break;
                }
              },
            },
          );
        });
      },
      'regenerateQuizItem',
    );
  }

  async setQuizGeneratorReaction(
    execution_id: string,
    reaction: REACTION,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v2ReactionsCreate(
        {
          context: CoreReactionContext.ReactionContextQuiz,
          entity_id: execution_id,
          // @ts-ignore
          reaction,
        },
        params,
      );
    }, 'setQuizGeneratorReaction');
  }

  async sendQuizQuestionAnswer(requestData: {
    executionId: string;
    questionId: string;
    optionsIds: string[];
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1QuizGeneratorQuizzesQuestionsOptionsCreate(
        requestData.executionId,
        requestData.questionId,
        requestData.optionsIds,
        params,
      );
    }, 'sendQuizQuestionAnswer');
  }

  async restartQuizGenerator(requestData: {
    executionId: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1QuizGeneratorQuizzesRestartCreate(
        requestData.executionId,
        params,
      );
    }, 'restartQuizGenerator');
  }

  async sendQuizGeneratorData(data: {
    text: string;
    count: number;
    type: string;
  }): Promise<QuizGeneratorExecutionType> {
    return this.makeAuthorizedRequest<QuizGeneratorExecutionType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v1/quiz-generator/quizzes`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify({
                text: data.text,
                question_type: data.type,
                number_of_questions: Number(data.count),
              }),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  );
                }

                switch (data.event_type) {
                  case 'quiz_generate_complete':
                    resolve(
                      PlatformApi.mapQuizGeneratorExecutionResponse(data.data),
                    );
                    break;
                }
              },
            },
          );
        });
      },
      'sendQuizGeneratorData',
    );
  }

  async getQuizGeneratorExecutions({
    query,
  }: {
    query: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_type?: string;
    };
  }): Promise<{
    data: QuizGeneratorExecutionType[];
    meta: MetaType;
  }> {
    return this.makeAuthorizedRequest<{
      data: QuizGeneratorExecutionType[];
      meta: MetaType;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1QuizGeneratorQuizzesList(
        query,
        params,
      );

      if (data) {
        const items: any = data.data;
        const meta = data.meta;
        return {
          data:
            items && items.length
              ? items.map(PlatformApi.mapQuizGeneratorExecutionResponse)
              : [],
          meta: {
            currentPage: meta?.current_page || query.page,
            pageSize: meta?.page_size || 20,
            totalCount: meta?.total_count || 0,
            totalPages: meta?.total_pages || 1,
          },
        };
      }
      throw Error('Invalid response');
    }, 'getQuizGeneratorExecution');
  }

  async getAiDetectorExecutionByID(
    id: string,
  ): Promise<AiDetectorExecutionType> {
    return this.makeAuthorizedRequest<AiDetectorExecutionType>(
      async (params) => {
        const { data } =
          await baseApiClient.api.v1ZerogptAiDetectorExecutionsDetail(
            id,
            params,
          );

        if (data.data) {
          return data.data;
        }

        throw Error('Invalid response');
      },
      'getAiDetectorExecutions',
    );
  }

  async deleteAiDetectorExecutions(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const response =
        await baseApiClient.api.v1ZerogptAiDetectorExecutionsDelete(params);

      if (response.status !== 200) {
        throw Error('Invalid response');
      }
    }, 'deleteAiDetectorExecution');
  }

  async sendAiDetectorReportEmail(id: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ZerogptAiDetectorExecutionsSendPdfReportCreate(
        id,
        params,
      );
    }, 'sendAiDetectorReportEmail');
  }

  async getAiDetectorFactChecking(id: string): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const data =
        await baseApiClient.api.v1ZerogptAiDetectorExecutionsFactCheckDetail(
          id,
          params,
        );

      const response = await data.json();

      if (response.data) {
        return response.data;
      }
      throw Error('Invalid response');
    }, 'getAiDetectorFactChecking');
  }

  async sendAiDetectorData(data: {
    input: string;
    // plagiarism_check_enabled?: boolean;
    primary_execution_id?: string;
    primary_tool_id?: string;
  }): Promise<ExtendAiDetectorExecutionType> {
    return this.makeAuthorizedRequest<ExtendAiDetectorExecutionType>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v1/zerogpt/ai-detector`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify(data),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(
                    new StreamVendorError(
                      data.error || 'no event data event_type',
                    ),
                  );
                }

                switch (data.event_type) {
                  case 'ai_detector_processing_complete': // TODO common mapper
                    resolve(
                      PlatformApi.mapAiDetectorExecutionResponse(data.data),
                    );
                    break;
                }
              },
            },
          );
        });
      },
      'sendAiDetectorData',
    );
  }

  async getAiDetector(): Promise<AiDetectorType> {
    return this.makeAuthorizedRequest<AiDetectorType>(async (params) => {
      const { data } = await baseApiClient.api.v1ZerogptAiDetectorList(params);

      if (data.data) {
        const item = data.data;
        return {
          id: item.id,
          free_humanize_count: item.free_humanize_count
            ? item.free_humanize_count
            : 0,
          title: item.title,
          last_inputs: {
            ...item.last_inputs,
            text: item.last_inputs.file_content || '',
          },
          is_limited: item.is_limited ?? false,
          ...PlatformApi.mapDetectorsWordsResponse(item),
        };
      }

      throw Error('Invalid response in getAiDetector');
    }, 'getAiDetector');
  }

  async getSubscription(): Promise<SubscriptionType> {
    return this.makeAuthorizedRequest<SubscriptionType>(async (params) => {
      const { data } = await baseApiClient.api.v1SubscriptionList(params);
      const subscription = data.data;
      if (subscription) {
        return PlatformApi.mapSubscriptionResponse(subscription);
      }
      throw Error('Invalid response in getSubscription');
    }, 'getSubscription');
  }

  async getBillingHistoryInvoices(): Promise<BillingHistoryInvoice[]> {
    return this.makeAuthorizedRequest<BillingHistoryInvoice[]>(
      async (params) => {
        const { data } =
          await baseApiClient.api.v1SubscriptionBillingHistoryInvoicesList(
            params,
          );
        return (data.data || []) as BillingHistoryInvoice[];
      },
      'getBillingHistoryInvoices',
    );
  }

  async getBillingHistoryOrderData(
    orderId: string,
  ): Promise<BillingHistoryOrderData> {
    return this.makeAuthorizedRequest<BillingHistoryOrderData>(
      async (params) => {
        const { data } =
          await baseApiClient.api.v1SubscriptionBillingHistoryOrdersDetail(
            orderId,
            params,
          );
        const orderData = data.data;
        if (orderData) {
          return orderData as BillingHistoryOrderData;
        }
        throw Error('Invalid response in getBillingHistoryOrderData');
      },
      'getBillingHistoryOrderData',
    );
  }

  async getPolicy(): Promise<PolicyType> {
    return this.makeAuthorizedRequest<PolicyType>(async (params) => {
      const { data } = await baseApiClient.api.v1ProfilePolicyList(params);
      const policy = data.data;
      if (policy) {
        return PlatformApi.mapPolicyResponse(policy);
      }
      throw Error('Invalid response in getPolicy');
    }, 'getPolicy');
  }

  async getProductList(): Promise<ProductType[]> {
    return this.makeAuthorizedRequest<ProductType[]>(async (params) => {
      const { data } = await baseApiClient.api.v1ProductsList(params);
      const products = data.data;
      if (products) {
        return products.map((product) =>
          PlatformApi.mapProductResponse(product),
        );
      }
      throw Error('Invalid response in getProductList');
    }, 'getProductList');
  }

  async updateUserProduct(productId: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1SubscriptionSwitchCreate(
        { product_id: productId },
        params,
      );
    }, 'updateUserProduct');
  }

  async claimRetainOffer(): Promise<{ next_charge_at?: string }> {
    return this.makeAuthorizedRequest<{ next_charge_at?: string }>(
      async (params) => {
        const { data } =
          await baseApiClient.api.v1SubscriptionRetainOfferCreate(params);
        return data.data ?? {};
      },
      'claimRetainOffer',
    );
  }

  async getProductExtraWordsList(): Promise<ProductType[]> {
    return this.makeAuthorizedRequest<ProductType[]>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ProductsOnetimeWordsBundlesList(params);
      const products = data.data;

      if (products) {
        return products.map((product) =>
          PlatformApi.mapProductResponse(product),
        );
      }

      if (products === null) {
        return [];
      }

      throw Error('Invalid response');
    }, 'getProductExtraWordsList');
  }

  async getProductById(id: string): Promise<ProductType> {
    return this.makeAuthorizedRequest<ProductType>(async (params) => {
      const { data } = await baseApiClient.api.v1ProductsDetail(id, params);
      const product = data.data;
      if (product) {
        return product;
      }
      throw Error('Invalid response in getProductById');
    }, 'getProductById');
  }

  async changePassword(
    old_password: string,
    new_password: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1PasswordChangeCreate(
        { old_password, new_password },
        params,
      );
    }, 'changePassword');
  }

  async updateProfile(payload: Record<string, any>): Promise<ProfileType> {
    return this.makeAuthorizedRequest<ProfileType>(async (params) => {
      const { data } = await baseApiClient.api.v1ProfileUpdate(payload, params);
      if (data.data) {
        return PlatformApi.mapProfileResponse(data.data);
      }
      throw Error('Invalid response in updateProfile');
    }, 'updateProfile');
  }

  async deleteAccount(user_id: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const sandbox = this.getPaymentSandboxKey(user_id);
      await baseApiClient.api.v1ProfileDelete({
        ...params, // @ts-ignore
        query: sandbox ? { sandbox } : {},
      });
    }, 'deleteAccount');
  }

  async updateOnboardingData(
    data: Partial<Record<string, string>>,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1AuthOnboardingV2Create(
        data as Parameters<
          typeof baseApiClient.api.v1AuthOnboardingV2Create
        >[0],
        params,
      );
    }, 'updateOnboardingData');
  }

  async uploadProfileAvatar(file: File): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const { data } = await baseApiClient.api.v1ProfileAvatarCreate(
        { file },
        params,
      );
      if (data?.data?.avatar_url) {
        return data.data.avatar_url;
      }
      throw Error('Invalid response');
    }, 'uploadProfileAvatar');
  }

  async removeProfileAvatar(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ProfileAvatarDelete(params);
    }, 'removeProfileAvatar');
  }

  async getUsageStatistics(): Promise<UsageStatisticsType> {
    return this.makeAuthorizedRequest<UsageStatisticsType>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ProfileUsageStatisticsList(params);

      if (data.data) {
        const user_last_tool =
          data.data.user_last_tool_id && data.data.user_last_tool_name
            ? {
                id: data.data.user_last_tool_id,
                name: data.data.user_last_tool_name,
              }
            : null;
        const user_popular_tool =
          data.data.user_popular_tool_id && data.data.user_popular_tool_name
            ? {
                id: data.data.user_popular_tool_id,
                name: data.data.user_popular_tool_name,
              }
            : null;
        return {
          user_last_tool,
          user_popular_tool,
          is_last_chat_used: data.data.user_last_tool_name === 'Chat',
          user_last_tools:
            data.data.user_last_tools?.map((item) =>
              PlatformApi.mapTemplateResponse(item),
            ) || [],
        };
      }
      throw Error('Invalid response');
    }, 'getUsageStatistics');
  }

  async restoreSubscription(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1SubscriptionRestoreCreate(params);
    }, 'restoreSubscription');
  }

  async restoreSubscriptionHard(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      return new Promise((resolve, reject): void => {
        const controller = new AbortController();
        fetchEventSource(
          `${baseApiClient.baseUrl}/api/v1/subscription/restore/hard`,
          {
            method: 'POST',
            openWhenHidden: true,
            signal: controller.signal,
            headers: {
              // @ts-ignore
              'Access-Token': params?.headers['Access-Token'] || '',
              ...this.getOriginOverrideHeaders(),
            },

            async onopen(response) {
              if (
                response.ok &&
                response.headers.get('content-type') === EventStreamContentType
              ) {
                return; // everything's good
              } else if (response.status === 401 || response.status === 403) {
                controller.abort();
                reject(new FatalError('Not Authorized'));
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
              ) {
                try {
                  const body = await response.json();
                  if (body.fields) {
                    throw new FieldsError(body);
                  }
                  throw new FatalError(body.error);
                } catch (e: any) {
                  controller.abort();
                  if (e instanceof FieldsError) {
                    reject(e);
                  }
                  reject(new FatalError(e.message || ''));
                }
              }
            },
            onmessage(event) {
              if (!event.data) {
                return; // keepalive, just waiting for another event
              }

              const data = JSON.parse(event.data);

              if (data.error || !data.event_type) {
                controller.abort();
                reject(
                  new StreamVendorError(
                    data.error || 'no event data event_type',
                  ),
                );
              }

              switch (data.event_type) {
                case ModelsChatEvent.RedemptionFixPaymentSuccessComplete:
                  resolve();
                  break;

                case ModelsChatEvent.RedemptionFixPaymentFailedComplete:
                  reject();
                  break;
              }
            },
          },
        );
      });
    }, 'restoreSubscriptionHard');
  }

  async cancelSubscription(
    body: {
      feedback: string;
      reason: string;
      notification_agreement?: boolean;
    },
    user_id: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      const sandbox = this.getPaymentSandboxKey(user_id);
      await baseApiClient.api.v1SubscriptionCancelCreate(body, {
        ...params,
        // @ts-ignore
        query: sandbox ? { sandbox } : {},
      });
    }, 'cancelSubscription');
  }

  async pauseSubscription(body: {
    feedback?: string;
    pause_amount: number;
    pause_unit: 'day' | 'week' | 'month';
    reason: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1SubscriptionPauseCreate(body, params);
    }, 'pauseSubscription');
  }

  async unpauseSubscription(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1SubscriptionPauseDelete(params);
    }, 'unpauseSubscription');
  }

  async skipTrialSubscription(): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1SubscriptionTrialFinishCreate(params);
    }, 'skipTrialSubscription');
  }

  async checkUserPassword(password: string): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1PasswordConfirmCreate({ password }, params);
    }, 'checkUserPassword');
  }

  async sendOffboardingSupportFeedback(feedback: {
    comment: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1OffboardingSupportCreate(feedback, params);
    }, 'sendOffboardingSupportFeedback');
  }

  async createFunnelUser(
    email: string,
    url?: string,
  ): Promise<{ id: string; email: string }> {
    try {
      const { data } = await baseApiClient.api.v1FunnelsUsersCreate(
        {
          email: email.trim(),
          ...(url && { url }),
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
      if (data.data) {
        return data.data;
      }

      throw Error('Invalid response');
    } catch (e) {
      throw await PlatformApi.handleError(e, 'createFunnelUser');
    }
  }

  async createFunnelPaymentIntentCard(
    user_id: string,
    product_id: string,
    geo_country?: string,
  ): Promise<{
    merchant: string;
    payment_intent: string;
    signature: string;
  }> {
    const { data } = await baseApiClient.api.v1FunnelsUsersPaymentsIntentCreate(
      user_id,
      {
        product_id,
        geo_country,
      },
      {
        user_fields: userFieldsService.userFields,
      },
      {
        headers: {
          ...this.getOriginOverrideHeaders(),
        },
      },
    );
    if (data.data) {
      return {
        merchant: data.data.merchant,
        payment_intent: data.data.payment_intent,
        signature: data.data.signature,
      };
    }
    throw Error('Invalid response in createFunnelPaymentIntentCard');
  }

  async createFunnelPaymentIntentPaypal(
    user_id: string,
    product_id: string,
  ): Promise<{
    order: Record<string, any>;
    order_metadata: any;
    pay_form: Record<string, any>;
  }> {
    const { data } =
      await baseApiClient.api.v1FunnelsUsersPaymentsPaypalV2Create(
        user_id,
        {
          product_id,
        },
        {
          user_fields: userFieldsService.userFields,
        },
        {
          headers: {
            ...this.getOriginOverrideHeaders(),
          },
        },
      );
    if (data.data) {
      return {
        order: data.data.order,
        order_metadata: data.data.order_metadata,
        pay_form: data.data.pay_form,
      };
    }
    throw Error('Invalid response in createFunnelPaymentIntentPaypal');
  }

  async getStripeEmbeddedPaymentIntent(
    product_id: string,
    body?: {
      redirect_url: string;
    },
  ): Promise<Record<string, any>> {
    return this.makeAuthorizedRequest<Record<string, any>>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ProductsPaymentsStripeCheckoutEmbeddedCreate(
          product_id,
          {
            ...(body || { redirect_url: '' }),
            user_fields: userFieldsService.userFields,
          },
          params,
        );

      if (data.data) {
        return data.data;
      }

      throw Error('Invalid response in getStripeEmbeddedPaymentIntent');
    }, 'getStripeEmbeddedPaymentIntent');
  }

  async getStripeSessionStatus(
    session_id: string,
  ): Promise<Record<string, any>> {
    return this.makeAuthorizedRequest<Record<string, any>>(async (params) => {
      const { data } =
        await baseApiClient.api.v1StripeCheckoutSessionsStatusDetail(
          session_id,
          params,
        );

      if (data.data) {
        return data.data;
      }

      throw Error('Invalid response in getStripeSessionStatus');
    }, 'getStripeSessionStatus');
  }

  async getStripeHostedForm(
    product_id: string,
    body: {
      redirect_url: string;
    },
  ): Promise<Record<string, any>> {
    return this.makeAuthorizedRequest<Record<string, any>>(async (params) => {
      const { data } =
        await baseApiClient.api.v1ProductsPaymentsStripeCheckoutHostedCreate(
          product_id,
          {
            ...body,
            user_fields: userFieldsService.userFields,
          },
          params,
        );

      if (data.data) {
        return data.data;
      }

      throw Error('Invalid response in getStripeHostedForm');
    }, 'getStripeHostedForm');
  }

  async createPaymentIntentCard(
    user_id: string,
    product_id: string,
    geo_country: string,
  ): Promise<{
    merchant: string;
    payment_intent: string;
    signature: string;
  }> {
    return this.makeAuthorizedRequest<{
      merchant: string;
      payment_intent: string;
      signature: string;
    }>(async (params) => {
      const sandbox = this.getPaymentSandboxKey(user_id);

      const { data } = await baseApiClient.api.v1ProductsPaymentsIntentCreate(
        product_id,
        {
          user_fields: userFieldsService.userFields,
        },
        {
          geo_country,
          ...(sandbox ? { sandbox } : {}),
        },
        params,
      );
      if (data.data) {
        return {
          merchant: data.data.merchant,
          payment_intent: data.data.payment_intent,
          signature: data.data.signature,
        };
      }
      throw Error('Invalid response in createPaymentIntentCard');
    }, 'createPaymentIntentCard');
  }

  async updatePaymentMethodCard(payload: {
    geo_country: string;
    token_type: UpdatePaymentTokenReason;
  }): Promise<{
    merchant: string;
    payment_intent: string;
    signature: string;
  }> {
    return this.makeAuthorizedRequest<{
      merchant: string;
      payment_intent: string;
      signature: string;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1PaymentsUpdateCardCreate(
        payload,
        params,
      );

      if (data.data) {
        return {
          merchant: data.data.merchant,
          payment_intent: data.data.payment_intent,
          signature: data.data.signature,
        };
      }
      throw Error('Invalid response in updatePaymentMethodCard');
    }, 'updatePaymentMethodCard');
  }

  async createPaymentIntentPaypal(
    user_id: string,
    product_id: string,
  ): Promise<{
    order: Record<string, any>;
    order_metadata: any;
    pay_form: Record<string, any>;
  }> {
    const sandbox = this.getPaymentSandboxKey(user_id);

    return this.makeAuthorizedRequest<{
      order: Record<string, any>;
      order_metadata: any;
      pay_form: Record<string, any>;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1ProductsPaymentsPaypalCreate(
        product_id,
        {
          user_fields: userFieldsService.userFields,
        },
        {
          ...params,
          // @ts-ignore
          query: sandbox ? { sandbox } : {},
        },
      );
      if (data.data) {
        return {
          order: data.data.order,
          order_metadata: data.data.order_metadata,
          pay_form: data.data.pay_form,
        };
      }
      throw Error('Invalid response in createPaymentIntentPaypal');
    }, 'createPaymentIntentPaypal');
  }

  async updatePaymentMethodPaypal(): Promise<{
    order: Record<string, any>;
    order_metadata: any;
    pay_form: Record<string, any>;
  }> {
    return this.makeAuthorizedRequest<{
      order: Record<string, any>;
      order_metadata: any;
      pay_form: Record<string, any>;
    }>(async (params) => {
      const { data } = await baseApiClient.api.v1PaymentsUpdatePaypalCreate({
        ...params,
      });

      if (data.data) {
        return {
          order: data.data.order,
          order_metadata: data.data.order_metadata,
          pay_form: data.data.pay_form,
        };
      }
      throw Error('Invalid response in updatePaymentMethodPaypal');
    }, 'updatePaymentMethodPaypal');
  }

  // TODO! 1542
  async checkUserPrepaidBenefits(data: {
    order_id: string;
  }): Promise<{ benefit: PrepaidBenefits }> {
    return this.makeAuthorizedRequest<{ benefit: PrepaidBenefits }>(
      async (params) => {
        return new Promise((resolve, reject): void => {
          const controller = new AbortController();
          fetchEventSource(
            `${baseApiClient.baseUrl}/api/v1/payments/orders/${data.order_id}/prepaid-benefit/stream`,
            {
              method: 'POST',
              openWhenHidden: true,
              signal: controller.signal,
              headers: {
                // @ts-ignore
                'Access-Token': params?.headers['Access-Token'] || '',
                ...this.getOriginOverrideHeaders(),
              },
              body: JSON.stringify(data),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type') ===
                    EventStreamContentType
                ) {
                  return; // everything's good
                } else if (response.status === 401 || response.status === 403) {
                  controller.abort();
                  reject(new FatalError('Not Authorized'));
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  try {
                    const body = await response.json();
                    if (body.fields) {
                      throw new FieldsError(body);
                    }
                    throw new FatalError(body.error);
                  } catch (e: any) {
                    controller.abort();
                    if (e instanceof FieldsError) {
                      reject(e);
                    }
                    reject(new FatalError(e.message || ''));
                  }
                }
              },
              onmessage(event) {
                if (!event.data) {
                  return; // keepalive, just waiting for another event
                }

                const data = JSON.parse(event.data);

                if (data.error || !data.event_type) {
                  controller.abort();
                  reject(new Error(data.error || 'no event data event_type'));
                }

                switch (data.event_type) {
                  case ModelsChatEvent.UserPrepaidBenefitReady:
                    resolve({
                      benefit: (data.data.benefit || '') as PrepaidBenefits,
                    });
                    break;
                }
              },
            },
          );
        });
      },
      'checkUserPrepaidBenefits',
    );
  }

  async createChatFeedback(
    formData: Partial<{
      understand_rate: number;
      experience_rate: number;
      responsiveness_rate: number;
      excitement_about: string;
      improvements: string;
      contact_consent: boolean;
    }>,
  ): Promise<{ id: string }> {
    return this.makeAuthorizedRequest<{ id: string }>(async (params) => {
      const { data } = await baseApiClient.api.v1ChatsFeedbacksCreate(
        {
          chat_understanding_rate: formData.understand_rate ?? undefined,
          experience_rate: formData.experience_rate ?? undefined,
          responsiveness_rate: formData.responsiveness_rate ?? undefined,
          favorite_chat_feature_aspect: formData.excitement_about ?? undefined,
          suggested_chat_improvements: formData.improvements ?? undefined,
          contact_consent: formData.contact_consent ?? undefined,
        },
        params,
      );

      if (data.data.id) {
        return {
          id: data.data.id,
        };
      }

      throw Error('Invalid response');
    }, 'createChatFeedback');
  }

  async updateChatFeedback(
    feedback_id: string,
    formData: Partial<{
      understand_rate: number;
      experience_rate: number;
      responsiveness_rate: number;
      excitement_about: string;
      improvements: string;
      contact_consent: boolean;
    }>,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ChatsFeedbacksUpdate(
        feedback_id,
        {
          favorite_chat_feature_aspect: formData.excitement_about ?? undefined,
          suggested_chat_improvements: formData.improvements ?? undefined,
          chat_understanding_rate: formData.understand_rate ?? undefined,
          experience_rate: formData.experience_rate ?? undefined,
          responsiveness_rate: formData.responsiveness_rate ?? undefined,
          contact_consent: formData.contact_consent ?? undefined,
        },
        params,
      );
    }, 'updateChatFeedback');
  }

  async setChatMessageReaction(
    chatId: string,
    messageId: string,
    reaction: REACTION,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ChatsMessagesReactionCreate(
        chatId,
        messageId,
        {
          reaction,
        },
        params,
      );
    }, 'setChatMessageReaction');
  }

  async markChatMessageCopied(
    chatId: string,
    messageId: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ChatsMessagesMarkCopiedCreate(
        chatId,
        messageId,
        params,
      );
    }, 'createChatMessageCopied');
  }

  async markToolsExecutionsResultsCopied(
    toolId: string,
    resultId: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsResultsMarkCopiedCreate(
        toolId,
        resultId,
        params,
      );
    }, 'createToolsExecutionsResultsCopied');
  }

  async markCopyleaksExecutionsResultsCopied(
    executionId: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1CopyleaksExportsCopyCreate(executionId, params);
    }, 'markCopyleaksExecutionsResultsCopied');
  }

  async markAiHumanizerExecutionsResultsCopied(
    executionId: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1AiHumanizerExecutionsCopyCreate(
        executionId,
        params,
      );
    }, 'markAiHumanizerExecutionsResultsCopied');
  }

  async markToolsAiEditCopied(
    aiEditId: string,
    toolId: string,
    resultId: string,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsResultsAiEditMarkCopiedCreate(
        aiEditId,
        toolId,
        resultId,
        params,
      );
    }, 'markToolsAiEditCopied');
  }

  async setToolResponseReaction(
    toolId: string,
    resultId: string,
    reaction: REACTION,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsResultsReactionCreate(
        toolId,
        resultId,
        {
          // @ts-ignore
          reaction,
        },
        params,
      );
    }, 'setToolResponseReaction');
  }

  async setToolAiEditReaction(
    toolId: string,
    resultId: string,
    aiEditId: string,
    reaction: REACTION,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsResultsAiEditReactionCreate(
        aiEditId,
        toolId,
        resultId,
        {
          // @ts-ignore
          reaction,
        },
        params,
      );
    }, 'setToolResponseReaction');
  }

  async createToolsFeedback(
    tool_id: string,
    formData: Partial<{
      understand_rate: number;
      experience_rate: number;
      responsiveness_rate: number;
      excitement_about: string;
      improvements: string;
      contact_consent: boolean;
    }>,
  ): Promise<{ id: string }> {
    return this.makeAuthorizedRequest<{ id: string }>(async (params) => {
      const { data } = await baseApiClient.api.v1ToolsFeedbacksCreate(
        tool_id,
        {
          excited_about: formData.excitement_about ?? undefined,
          experience_rate: formData.experience_rate ?? undefined,
          responsiveness_rate: formData.responsiveness_rate ?? undefined,
          suggested_improvements: formData.improvements ?? undefined,
          tool_understanding_rate: formData.understand_rate ?? undefined,
          contact_consent: formData.contact_consent ?? undefined,
        },
        params,
      );

      if (data.data.id) {
        return {
          id: data.data.id,
        };
      }

      throw Error('Invalid response');
    }, 'createToolsFeedback');
  }

  async updateToolsFeedback(
    tool_id: string,
    feedback_id: string,
    formData: Partial<{
      understand_rate: number;
      experience_rate: number;
      responsiveness_rate: number;
      excitement_about: string;
      improvements: string;
      contact_consent: boolean;
    }>,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsFeedbacksUpdate(
        feedback_id,
        tool_id,
        {
          excited_about: formData.excitement_about ?? undefined,
          experience_rate: formData.experience_rate ?? undefined,
          responsiveness_rate: formData.responsiveness_rate ?? undefined,
          suggested_improvements: formData.improvements ?? undefined,
          tool_understanding_rate: formData.understand_rate ?? undefined,
          contact_consent: formData.contact_consent ?? undefined,
        },
        params,
      );
    }, 'updateToolsFeedback');
  }

  async createNeedToolFeedback(
    formData: Partial<{
      name: string;
      description: string;
      contact_consent: boolean;
    }>,
  ): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ToolsFeedbacksCreate2(
        {
          contact_consent: formData.contact_consent ?? undefined,
          wanted_tool_description: formData.description ?? undefined,
          wanted_tool_name: formData.name ?? undefined,
        },
        params,
      );
    }, 'createNeedToolFeedback');
  }

  async getVersion(): Promise<{ github_sha: string; need_update: boolean }> {
    try {
      const response = await fetch('/version.json');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data.github_sha) {
        throw new Error('github_sha not found in version file');
      }
      return {
        github_sha: data.github_sha,
        need_update: data.need_update || false,
      };
    } catch (error: any) {
      return {
        github_sha: 'unknown',
        need_update: false,
      };
    }
  }

  async sendRateFeedback(data: {
    chat_id?: string;
    id?: string;
    rate?: number;
    response?: string;
    slug?: string;
    tool_id?: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1FeedbacksV2RateBarsCreate(data, params);
    }, 'sendRateFeedback');
  }

  async sendOutputFeedback(dataToSend: {
    chat_id?: string;
    comment?: string;
    rate?: number;
    id?: string;
    reasons?: string[];
    response?: string;
    slug?: string;
    tool_id?: string;
    execution_id?: string;
  }): Promise<string> {
    return this.makeAuthorizedRequest<string>(async (params) => {
      const { data } = await baseApiClient.api.v1FeedbacksV2OutputsCreate(
        dataToSend,
        params,
      );

      if (data.data.id) {
        return data.data.id;
      }
      throw Error('Invalid response');
    }, 'sendOutputFeedback');
  }

  async sendFeatureFeedback(data: {
    chat_id?: string;
    feature_name?: string;
    id?: string;
    rate?: number;
    response?: string;
    slug?: string;
    tool_id?: string;
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1FeedbacksV2FeaturesCreate(data, params);
    }, 'sendFeatureFeedback');
  }

  async sendExtensionPostFeedback(data: {
    user_id?: string;
    reasons?: string[];
    suggestion?: string;
  }): Promise<void> {
    try {
      await baseApiClient.api.v1ChromeExtensionPostDeleteFeedbackCreate({
        ...(data.suggestion ? { suggestion: data.suggestion } : {}),
        ...(data.reasons ? { reasons: data.reasons } : {}),
        ...(data.user_id ? { user_id: data.user_id } : {}),
      });
    } catch (error) {
      throw await PlatformApi.handleError(error, 'sendExtensionPostFeedback');
    }
  }

  async sendExtensionLifecycleEvent(data: {
    status: 'installed' | 'deleted';
  }): Promise<void> {
    return this.makeAuthorizedRequest<void>(async (params) => {
      await baseApiClient.api.v1ChromeExtensionLifecycleCreate(data, params);
    }, 'sendExtensionLifecycleEvent');
  }

  async getCountry(): Promise<string> {
    try {
      const { data } = await baseApiClient.api.v1GeolocationList();
      if (!data.data) {
        throw new Error('Country code not found in response');
      }
      analytics.trackEvent('temp - get country code success', {
        country: data.data,
      });
      return data.data;
    } catch (error) {
      analytics.trackEvent('temp - get country code error');
      return 'US';
    }
  }

  async trackEmailClick(data: { user_id: string }): Promise<void> {
    await baseApiClient.api.v1EmailClickCreate(
      {
        user_id: data.user_id,
        user_fields: userFieldsService.userFields,
      },
      {
        keepalive: true,
      },
    );
  }

  async checkGrammar(text: string): Promise<GrammarCheckResult> {
    return this.makeAuthorizedRequest<GrammarCheckResult>(async (params) => {
      const response = await fetch(
        `${baseApiClient.baseUrl}/api/proxy/linguix/v1/checker`,
        {
          body: JSON.stringify({ text }),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...params.headers,
          },
        },
      );
      return response.json();
    }, 'checkGrammar');
  }
}

export default new PlatformApi({
  storage: localStorage,
  notAuthorizedCallback: (): void => {
    if (localStorage.getByKey('isDevMode')) {
      console.warn('reload page');
    } else {
      const pathname = new URL(window.location.href).pathname;

      if (
        ![
          '/sign-in',
          '/sign-up',
          '/reset-password',
          '/forgot-password',
          '/offboarding-extension',
        ].includes(pathname)
      ) {
        window.location.href =
          pathname && pathname !== '/'
            ? `/sign-in?redirect_path=${encodeURIComponent(pathname)}`
            : '/sign-in';
      }
    }
  },
});
